// frontend/grautomates/src/components/public/VendorPrequalApply.tsx
// Public (unauthenticated) Vendor Prequalification form.
// Mounted standalone at /vendor-prequal/apply — no app shell, no sidebar.
//
// Phase 1: Basic + Low-risk (Parts 1-3). High-risk (Parts 4-6) is stubbed
// for Phase 2. All file uploads are collected client-side and posted as
// multipart/form-data to POST /api/vendor-prequal/submit.
import { usePublicPageScroll } from '../../hooks/usePublicPageScroll';
import { useEffect, useMemo, useRef, useState } from 'react';
import { HIGH_RISK_PARTS } from './vendorPrequal/highRiskSchema';
import HighRiskForm from './vendorPrequal/HighRiskForm';
import FileDropZone from './vendorPrequal/FileDropZone';
import { getAuthToken } from '../../utils/auth';
import { API_BASE, IS_DEV } from '../../config/apiConfig';
import Select from '../shared/Select';
import Modal, { ModalBody, ModalFooter } from '../shared/Modal';
import Button from '../shared/Button';
import Alert from '../shared/Alert';
import { CHECKBOX_CLASS, FIELD_LABEL_CLASS } from '../shared/FormInput';
import { QueueChip } from '../shared/ApprovalQueue';
import { TEXT } from '../shared/Typography';

// ---------------------------------------------------------------------------
// Local draft autosave + network resilience
//
// The public form used to hold every typed answer in React memory only, so a
// page refresh or a dropped network save wiped everything the vendor entered.
// We now mirror the answers to localStorage (debounced) so a refresh/crash is
// non-destructive. Files can't be serialised, so the "Save & Close" server
// draft remains the mechanism that carries attachments across sessions.
// ---------------------------------------------------------------------------
export const AUTOSAVE_KEY = 'vpq-apply-autosave-v1';
const AUTOSAVE_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

/**
 * fetch with an abort-based timeout. Uploads can be slow on poor vendor
 * connections, so the timeout is deliberately generous — its job is to surface
 * a clear, reassuring message instead of the request hanging indefinitely.
 */
async function fetchWithTimeout(
  url: string,
  options: RequestInit,
  timeoutMs = 180000
): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Turn a fetch/HTTP failure into a human message. Network-level failures
 * (offline, DNS, connection reset, timeout) surface as a TypeError
 * ("Failed to fetch") or an AbortError — reassure the vendor their answers are
 * safe locally so they don't refresh and lose work.
 */
export function describeSaveError(err: unknown, answersSavedLocally: boolean): string {
  const e = err as { name?: string; message?: string };
  const isTimeout = e?.name === 'AbortError';
  const isNetwork =
    isTimeout ||
    e?.name === 'TypeError' ||
    (typeof e?.message === 'string' && /failed to fetch|network|load failed/i.test(e.message));
  if (isNetwork) {
    const base = isTimeout
      ? 'The request timed out before the server responded.'
      : 'We could not reach the server.';
    return answersSavedLocally
      ? `${base} Your answers are saved on this device — check your internet connection and try again. Nothing you typed has been lost.`
      : `${base} Please check your internet connection and try again.`;
  }
  return e?.message || 'Something went wrong. Please try again.';
}

// ---------------------------------------------------------------------------
// Option lists (from the Jotform)
// ---------------------------------------------------------------------------
const PRODUCTS_SERVICES: string[] = [
  'Aviation', 'Catering', 'Chemicals', 'Civil Earthworks', 'Cleaning equipment',
  'Concreting equipment', 'Confined Space Entry', 'Containers and Storage', 'Demolition',
  'Domestic freight services', 'Drilling and Boring equipment', 'Electrical Equipment',
  'Engineering consultants', 'Fabrication', 'Fuels', 'Generators', 'Heavy Vehicles',
  'Hose repair', 'HVAC', 'Hydraulic Tooling', 'Inspection, Testing, Certification, Calibration',
  'International freight services', 'IT and Communications', 'Lifting and rigging',
  'Light Vehicles', 'Lighting equipment', 'Marine agency and port services',
  'Materials Handing Equipment', 'Medical Supplies', 'NORMs handling', 'Office equipment',
  'Offshore rental equipment', 'Onshore rental equipment', 'Other consultants', 'Other tools',
  'Packaging materials', 'Painting and blasting', 'Personnel Transport',
  'Piling and Foundation',
  'Pipe Fittings (Studs, Bolts, Gaskets, O-rings, piping, tubing, flanges, blinds)',
  'Pipe suppliers', 'Plumbing Services', 'Pneumatic Tooling', 'PPE', 'Pressure Metering',
  'Process Plant Supply', 'Pump supply', 'Safety Equipment', 'Scaffolding and access',
  'Site amenities', 'Site Technicians', 'Spare parts', 'Subsea equipment', 'Traffic Control',
  'Training and Education', 'Valve supply', 'Vessel Hire', 'Waste handling',
  'Welding and Hot Work', 'Working at Heights',
];

const AUS_STATES = ['ACT', 'NSW', 'NT', 'QLD', 'SA', 'TAS', 'VIC', 'WA'];

const INSURANCE_TYPES = [
  {
    key: 'generalThirdPartyLiability',
    label: 'General Third Party & Liability',
    minimum: 'Per Occurrence $20,000,000',
    requiredWhen: 'Vendor provides goods or services to GRPS.',
  },
  {
    key: 'employersLiability',
    label: "Employer's Liability / Statutory Worker's Compensation",
    minimum:
      'Comply with State & Federal Laws. Inc. Extended Cover for Offshore Work (if relevant).',
    requiredWhen: 'Vendor personnel enter GRPS and/or GRPS client premises.',
  },
  {
    key: 'professionalIndemnity',
    label: 'Professional Indemnity',
    minimum: 'Per Occurrence $20,000,000',
    requiredWhen: 'Vendor provides professional services incl. engineering and related advice.',
  },
  {
    key: 'professionalIndemnitySoleTrader',
    label: 'Professional Indemnity (Sole Trader)',
    minimum: 'Per Occurrence $10,000,000',
    requiredWhen: 'Vendor is a Sole Trader incl. Electricians & Mechanical Technicians.',
  },
  {
    key: 'motorVehicle',
    label: 'Motor Vehicle / Passenger Liability',
    minimum: 'Per Occurrence $10,000,000',
    requiredWhen: 'Vendor operates motor vehicles at GRPS and/or GRPS client premises.',
  },
  {
    key: 'propertyEquipment',
    label: 'Property & Equipment Insurance',
    minimum: 'Per incident Market Value',
    requiredWhen: 'Vendor operates plant/equipment at GRPS and/or GRPS client premises.',
  },
] as const;

// Authoritative text copied from the Jotform T&C widgets (QIDs 641-648).
const CORPORATE_RESPONSIBILITY_STATEMENTS: string[] = [
  'We are against Forced or Compulsory labor: The supplier must ensure there is no forced, bonded or involuntary labor.',
  'We are against Child Labor: Children must not be hired to work before completing their compulsory education (as determined by applicable local laws). Notwithstanding local requirements, the minimum age for entry into employment must not be younger than 15 years of age.',
  'We allow Freedom of association: The supplier must adopt an open attitude towards the legitimate activities of trade unions, allow their workers’ representatives to carry out their legitimate representative functions in the workplace and ensure they are not discriminated against.',
  'Wages and benefits paid for a standard working week must satisfy, at a minimum, national legal standards or local industry benchmarks, whichever is higher. In nation states where no minimum wage legislation exists, the supplier must seek to establish a living wage that provides an adequate standard of living for all its employees and their dependants.',
  'We promote Anti-discrimination and diversity: The supplier must provide a work environment in which everyone is treated fairly and cultural, ethnic, religious or other diversity factors such as gender are respected. Offer employment based on merit. Not base its decisions regarding employment on attributes unrelated to job performance (including but not limited to race, colour, gender, religion, personal associations, national origin, age, disability, political beliefs, marital status, sexual orientation and family responsibilities). Decisions relating to suppliers, customers, contractors and other stakeholders must also be based on merit.',
  'We promote Workplace health and safety: The supplier must provide safe and healthy working facilities and appropriate precautionary measures to protect employees from work-related hazards; workers with regular and recorded health and safety training; clean and safe accommodation that meets the basic needs of workers (where applicable). The supplier must follow all relevant legislation and implement systems for the prevention of occupational injury and illness, including fire safety standards, emergency preparedness and response, occupational/industrial hygiene standards, appropriate lighting and ventilation, machinery safeguarding, and reporting/recording of incidents with investigation and follow-up close-out actions.',
  'We promote Fair Treatment of employees and communities: The supplier must create and maintain an environment that treats all employees with dignity and respect and will not use any threats of violence, sexual exploitation or abuse, or verbal or psychological harassment or abuse.',
  'We promote trade compliance: Does your company have policies and procedures in place regarding business conduct and compliance? (anti-corruption, anti-extortion and anti-bribery).',
];

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
type PrequalType = 'Basic' | 'Low-risk' | 'High-risk';

interface ContactData {
  name: string;
  jobTitle: string;
  email: string;
  contactNumber: string;
}

interface DesignatedContact extends ContactData {
  isPrimary: boolean;
}

interface InsuranceBlock {
  meetsMinimum: boolean;
  // Basic prequalification only (Part 3): does the vendor hold public
  // liability cover at all. null until answered.
  held: boolean | null;
  limitsText: string;
  // Currency of the cover, not just its existence. Without these, GreenTree's
  // insurance expiry fields could only ever be typed in by hand off the
  // certificate, and nothing refreshed them afterwards.
  expiryDate: string; // yyyy-mm-dd
  insurer: string;
  policyNumber: string;
}

interface ExperienceRow {
  contractProject: string;
  client: string;
  goodsServices: string;
  timeline: string;
  contractValue: string;
}

interface FormState {
  prequalificationType: PrequalType | '';
  grpsContactName: string;
  company: {
    legalName: string;
    tradingName: string;
    yearFounded: string;
    phoneNumber: string;
    abn: string;
    websiteUrl: string;
  };
  headOffice: {
    streetAddress: string;
    suburb: string;
    postcode: string;
    state: string;
    country: string;
  };
  designatedContact: ContactData;
  designatedContacts: DesignatedContact[];
  contractsSalesContact: ContactData;
  accountsRemittanceContact: Omit<ContactData, 'contactNumber'>;
  additionalQuestions: {
    registeredForGst: boolean;
    providesHazardousMaterials: boolean;
    hasReconciliationActionPlan: boolean;
    qualifiesAsIndigenousBusiness: boolean;
    supplyNationStatus: 'Certified' | 'Registered' | 'N/A';
    productsServices: string[];
    employeeCount: string;
    currentlyApprovedSupplier: boolean;
    yearsSupplyingToGrps: string;
  };
  terms: {
    hasActiveLitigation: boolean;
    litigationDetails: string;
    /** Derived from purchaseOrderTermsStatus; kept for existing consumers. */
    purchaseOrderTermsAccepted: boolean;
    /** '' until the vendor answers. Never defaulted to 'accepted'. */
    purchaseOrderTermsStatus: PurchaseOrderTermsStatus;
    purchaseOrderTermsDeviationNotes: string;
    /** High-risk only: accepted the GRPS Service Agreement, so the PO terms are not asked. */
    serviceAgreementAccepted: boolean;
  };
  insurances: Record<(typeof INSURANCE_TYPES)[number]['key'], InsuranceBlock>;
  additionalInsuranceDetails: string;
  banking: {
    accountName: string;
    bank: string;
    bsb: string;
    accountNumber: string;
    countryOfAccount: string;
    institutionNumber: string;
    transitNumber: string;
    swift: string;
    iban: string;
  };
  experience: ExperienceRow[];
  subSubcontractors: { sublets: boolean; details: string };
  corporateResponsibilityAcceptances: boolean[];
}

/**
 * The vendor's answer on the Standard Purchase Order Terms.
 *
 * '' is "not answered yet" and is the starting value. It is deliberately not
 * 'accepted': a default would record an acceptance the vendor never made.
 */
export type PurchaseOrderTermsStatus = '' | 'accepted' | 'deviation' | 'service_agreement';

type FileMap = Record<string, File[]>;

interface DraftAttachment {
  _id: string;
  fieldKey: string;
  filename: string;
  mimeType?: string;
  size: number;
  uploadedAt: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
// Bank field sanitisers — dashes/spaces must never be stored (procurement.txt #6).
const digitsOnly = (v: string): string => (v || '').replace(/\D/g, '');
const alnumOnly = (v: string): string => (v || '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();

/**
 * Account number rule: letters and numbers, any length. Vendors legitimately
 * put SWIFT/BIC and IBAN values in this field, so it is not restricted to
 * digits and carries no length cap. What it does reject is special characters —
 * the spaces and dashes that made the originally-reported values unusable.
 * Kept in step with backend/utils/bankAccountValidation.ts.
 */

/**
 * Re-sanitise banking identifiers coming back out of localStorage. A draft saved
 * before these inputs sanitised restores straight into state without passing
 * through onChange, which is one of the ways an unsanitised value could still
 * reach the server. Returns a partial to spread over the restored state;
 * `{}` when there is no banking block to fix.
 */
function sanitiseRestoredBanking(saved: Record<string, unknown>): Record<string, unknown> {
  const banking = saved?.banking as Record<string, unknown> | undefined;
  if (!banking || typeof banking !== 'object') return {};
  const str = (v: unknown): string => (typeof v === 'string' ? v : '');
  return {
    banking: {
      ...banking,
      bsb: digitsOnly(str(banking.bsb)).slice(0, 6),
      accountNumber: alnumOnly(str(banking.accountNumber)),
      institutionNumber: digitsOnly(str(banking.institutionNumber)),
      transitNumber: digitsOnly(str(banking.transitNumber)),
      swift: alnumOnly(str(banking.swift)),
      iban: alnumOnly(str(banking.iban)),
    },
  };
}
// AU if the account country (fallback: head-office country) starts with "aus".
const isAustralianAccount = (accountCountry: string, headOfficeCountry: string): boolean => {
  const c = (accountCountry || headOfficeCountry || '').trim().toLowerCase();
  return c === '' || c.startsWith('aus');
};

const emptyContact = (): ContactData => ({ name: '', jobTitle: '', email: '', contactNumber: '' });
const emptyDesignatedContact = (isPrimary = false): DesignatedContact => ({
  name: '', jobTitle: '', email: '', contactNumber: '', isPrimary,
});
const primaryOf = (contacts: DesignatedContact[]): DesignatedContact | undefined =>
  contacts.find(c => c.isPrimary) || contacts[0];

// Build the designatedContacts array from a loaded submission/draft: prefer the
// stored array; fall back to wrapping the legacy single contact; else one empty.
function hydrateDesignatedContacts(src: any): DesignatedContact[] {
  const arr = src?.designatedContacts;
  if (Array.isArray(arr) && arr.length) {
    const mapped: DesignatedContact[] = arr.map((c: any) => ({
      name: c?.name || '', jobTitle: c?.jobTitle || '', email: c?.email || '',
      contactNumber: c?.contactNumber || '', isPrimary: !!c?.isPrimary,
    }));
    if (!mapped.some(c => c.isPrimary)) mapped[0].isPrimary = true;
    return mapped;
  }
  const d = src?.designatedContact;
  if (d && (d.name || d.email)) {
    return [{
      name: d.name || '', jobTitle: d.jobTitle || '', email: d.email || '',
      contactNumber: d.contactNumber || '', isPrimary: true,
    }];
  }
  return [emptyDesignatedContact(true)];
}
const emptyInsurance = (): InsuranceBlock => ({
  meetsMinimum: false,
  held: null,
  limitsText: '',
  expiryDate: '',
  insurer: '',
  policyNumber: '',
});
const emptyExperienceRow = (): ExperienceRow => ({
  contractProject: '',
  client: '',
  goodsServices: '',
  timeline: '',
  contractValue: '',
});

function initialState(): FormState {
  return {
    prequalificationType: '',
    grpsContactName: '',
    company: {
      legalName: '', tradingName: '', yearFounded: '', phoneNumber: '', abn: '', websiteUrl: '',
    },
    headOffice: {
      streetAddress: '', suburb: '', postcode: '', state: '', country: 'Australia',
    },
    designatedContact: emptyContact(),
    designatedContacts: [emptyDesignatedContact(true)],
    contractsSalesContact: emptyContact(),
    accountsRemittanceContact: { name: '', jobTitle: '', email: '' },
    additionalQuestions: {
      registeredForGst: false,
      providesHazardousMaterials: false,
      hasReconciliationActionPlan: false,
      qualifiesAsIndigenousBusiness: false,
      supplyNationStatus: 'N/A',
      productsServices: [],
      employeeCount: '',
      currentlyApprovedSupplier: false,
      yearsSupplyingToGrps: '',
    },
    terms: {
      hasActiveLitigation: false,
      litigationDetails: '',
      purchaseOrderTermsAccepted: false,
      purchaseOrderTermsStatus: '',
      serviceAgreementAccepted: false,
      purchaseOrderTermsDeviationNotes: '',
    },
    insurances: {
      generalThirdPartyLiability: emptyInsurance(),
      employersLiability: emptyInsurance(),
      professionalIndemnity: emptyInsurance(),
      professionalIndemnitySoleTrader: emptyInsurance(),
      motorVehicle: emptyInsurance(),
      propertyEquipment: emptyInsurance(),
    },
    additionalInsuranceDetails: '',
    banking: {
      accountName: '', bank: '', bsb: '', accountNumber: '',
      countryOfAccount: '', institutionNumber: '', transitNumber: '', swift: '', iban: '',
    },
    experience: [emptyExperienceRow()],
    subSubcontractors: { sublets: false, details: '' },
    corporateResponsibilityAcceptances: new Array(8).fill(false),
  };
}

// ---------------------------------------------------------------------------
// UI primitives
// ---------------------------------------------------------------------------
function Label({ children, required }: { children: React.ReactNode; required?: boolean }) {
  return (
    <label className={FIELD_LABEL_CLASS}>
      {children} {required && <span className="text-red-600">*</span>}
    </label>
  );
}

function TextInput({
  value,
  onChange,
  type = 'text',
  placeholder,
  invalid = false,
  maxLength,
  inputMode,
}: {
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  invalid?: boolean;
  /** Hard character cap. Stops over-long entry at the keyboard rather than
   *  only reporting it on submit. */
  maxLength?: number;
  /** Surfaces a numeric keypad on mobile for digits-only fields. */
  inputMode?: 'text' | 'numeric' | 'tel' | 'email' | 'url' | 'decimal' | 'search';
}) {
  return (
    <input
      type={type}
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      maxLength={maxLength}
      inputMode={inputMode}
      aria-invalid={invalid || undefined}
      className={`w-full rounded-md border px-3 py-2 text-base focus:outline-none focus:ring-2 ${
        invalid ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'
      }`}
    />
  );
}

// Small inline field-error line shown beneath a required control.
function FieldError({ show, children }: { show: boolean; children: React.ReactNode }) {
  if (!show) return null;
  return <p className="mt-1 text-sm text-red-600">{children}</p>;
}

function TextArea({
  value,
  onChange,
  rows = 3,
}: {
  value: string;
  onChange: (v: string) => void;
  rows?: number;
}) {
  return (
    <textarea
      value={value}
      onChange={e => onChange(e.target.value)}
      rows={rows}
      className="w-full rounded-md border border-gray-300 px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
    />
  );
}

function YesNo({ value, onChange }: { value: boolean | null; onChange: (v: boolean) => void }) {
  return (
    <div className="flex gap-4">
      <label className="flex items-center gap-2">
        <input type="radio" checked={value === true} onChange={() => onChange(true)} /> Yes
      </label>
      <label className="flex items-center gap-2">
        <input type="radio" checked={value === false} onChange={() => onChange(false)} /> No
      </label>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
      <h2 className="text-xl font-semibold text-blue-900 mb-4 border-b pb-2">{title}</h2>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function VendorPrequalApply(): JSX.Element {
  const [state, setState] = useState<FormState>(initialState);
  const [highRisk, setHighRisk] = useState<Record<string, any>>({});
  const [files, setFiles] = useState<FileMap>({});
  const [step, setStep] = useState<number>(0);
  const [submitting, setSubmitting] = useState(false);
  const [submittedId, setSubmittedId] = useState<string | null>(null);
  const [error, setError] = useState<string>('');

  // Draft (Save & Close) state
  const [draftToken, setDraftToken] = useState<string | null>(null);
  // Read by the async restore, which must not replace a draft saved meanwhile.
  const draftTokenRef = useRef<string | null>(null);
  draftTokenRef.current = draftToken;
  const [draftAttachments, setDraftAttachments] = useState<DraftAttachment[]>([]);
  const [removedDraftAttIds, setRemovedDraftAttIds] = useState<string[]>([]);
  const [draftHydrating, setDraftHydrating] = useState(false);
  const [draftHydrateError, setDraftHydrateError] = useState<string>('');

  // Local autosave applies only to a fresh public application — not admin edit
  // (`?adminEdit`, edits a live record) or server-draft resume (`?resume`,
  // already persisted server-side). Captured once on mount.
  const [autosaveMode] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    const p = new URLSearchParams(window.location.search);
    return !p.get('adminEdit') && !p.get('resume');
  });
  const [restoredFromLocal, setRestoredFromLocal] = useState(false);
  // How many saved files came back with a restored draft (see the restore effect).
  const [restoredDraftFiles, setRestoredDraftFiles] = useState(0);
  // A "more information required" round-trip on an existing submission, which
  // is exempt from rules introduced for new vendors.
  const [isResubmission, setIsResubmission] = useState(false);
  // Why procurement sent the application back (rejected / more info), shown on top.
  const [sentBack, setSentBack] = useState<{ kind: 'more_info' | 'rejected'; message: string } | null>(null);
  const persistReadyRef = useRef(false);

  // Keys of required fields that failed validation on the current step, used to
  // highlight the offending controls (e.g. 'grpsContactName',
  // 'company.legalName', 'terms.purchaseOrderTermsAccepted').
  const [fieldErrors, setFieldErrors] = useState<Set<string>>(new Set());

  // Admin edit mode: form is hydrated from an existing submission and Save
  // posts back to PUT /api/vendor-prequal/:id with the admin's JWT.
  const [adminEditId, setAdminEditId] = useState<string | null>(null);
  const [adminEditSaved, setAdminEditSaved] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveModalEmail, setSaveModalEmail] = useState('');
  const [saveResult, setSaveResult] = useState<{
    link: string;
    sentTo: string;
    emailSent: boolean;
  } | null>(null);

  const apiBase = API_BASE;

  // Admin edit mode: ?adminEdit=<submissionId> on mount.
  // Uses authed fetch — the admin's JWT comes from the JWT cookie/header set
  // up by axios's global interceptor on the rest of the app. Here we fetch
  // raw so we can include the Authorization header explicitly from
  // localStorage if present.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const editId = params.get('adminEdit');
    if (!editId) return;
    let cancelled = false;
    setDraftHydrating(true);
    (async () => {
      try {
        const token = getAuthToken() || '';
        const headers: Record<string, string> = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;
        const res = await fetch(`${apiBase}/api/vendor-prequal/${encodeURIComponent(editId)}`, {
          headers,
        });
        const data = await res.json();
        if (!res.ok || !data.success) {
          throw new Error(data.error || `Could not load submission (HTTP ${res.status})`);
        }
        if (cancelled) return;
        const sub: any = data.data || {};
        setState(prev => ({
          ...prev,
          prequalificationType: sub.prequalificationType || prev.prequalificationType,
          grpsContactName: sub.grpsContactName || '',
          company: { ...prev.company, ...(sub.company || {}) },
          headOffice: { ...prev.headOffice, ...(sub.headOffice || {}) },
          designatedContact: { ...prev.designatedContact, ...(sub.designatedContact || {}) },
          designatedContacts: hydrateDesignatedContacts(sub),
          contractsSalesContact: {
            ...prev.contractsSalesContact,
            ...(sub.contractsSalesContact || {}),
          },
          accountsRemittanceContact: {
            ...prev.accountsRemittanceContact,
            ...(sub.accountsRemittanceContact || {}),
          },
          additionalQuestions: {
            ...prev.additionalQuestions,
            ...(sub.additionalQuestions || {}),
          },
          terms: { ...prev.terms, ...(sub.terms || {}) },
          insurances: { ...prev.insurances, ...(sub.insurances || {}) },
          additionalInsuranceDetails:
            sub.additionalInsuranceDetails || prev.additionalInsuranceDetails,
          banking: { ...prev.banking, ...(sub.banking || {}) },
          experience:
            Array.isArray(sub.experience) && sub.experience.length
              ? sub.experience
              : prev.experience,
          subSubcontractors: { ...prev.subSubcontractors, ...(sub.subSubcontractors || {}) },
          corporateResponsibilityAcceptances:
            Array.isArray(sub.corporateResponsibilityAcceptances) &&
            sub.corporateResponsibilityAcceptances.length === 8
              ? sub.corporateResponsibilityAcceptances
              : prev.corporateResponsibilityAcceptances,
        }));
        if (sub.highRisk && typeof sub.highRisk === 'object') {
          setHighRisk(sub.highRisk);
        }
        // Existing attachments — show in the "previously uploaded" panel
        const atts: DraftAttachment[] = (sub.attachments || []).map((a: any) => ({
          _id: String(a._id),
          fieldKey: a.fieldKey,
          filename: a.filename,
          mimeType: a.mimeType,
          size: a.size,
          uploadedAt: a.uploadedAt,
        }));
        setDraftAttachments(atts);
        setAdminEditId(editId);
      } catch (err: any) {
        if (cancelled) return;
        setDraftHydrateError(err?.message || 'Could not load submission for editing');
      } finally {
        if (!cancelled) setDraftHydrating(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiBase]);

  // Resume from ?resume=<token> on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('resume');
    if (!token) return;
    let cancelled = false;
    setDraftHydrating(true);
    (async () => {
      try {
        const res = await fetch(`${apiBase}/api/vendor-prequal/draft/${encodeURIComponent(token)}`);
        const data = await res.json();
        if (!res.ok || !data.success) {
          throw new Error(data.error || `Could not load draft (HTTP ${res.status})`);
        }
        if (cancelled) return;
        const fs: any = data.data?.formState || {};
        // Merge into initial state so any new fields added since the draft was
        // saved still get sensible defaults.
        setState(prev => ({
          ...prev,
          ...fs,
          company: { ...prev.company, ...(fs.company || {}) },
          headOffice: { ...prev.headOffice, ...(fs.headOffice || {}) },
          designatedContact: { ...prev.designatedContact, ...(fs.designatedContact || {}) },
          designatedContacts: hydrateDesignatedContacts(fs),
          contractsSalesContact: {
            ...prev.contractsSalesContact,
            ...(fs.contractsSalesContact || {}),
          },
          accountsRemittanceContact: {
            ...prev.accountsRemittanceContact,
            ...(fs.accountsRemittanceContact || {}),
          },
          additionalQuestions: {
            ...prev.additionalQuestions,
            ...(fs.additionalQuestions || {}),
          },
          terms: { ...prev.terms, ...(fs.terms || {}) },
          insurances: { ...prev.insurances, ...(fs.insurances || {}) },
          banking: { ...prev.banking, ...(fs.banking || {}) },
          subSubcontractors: { ...prev.subSubcontractors, ...(fs.subSubcontractors || {}) },
        }));
        if (fs.highRisk && typeof fs.highRisk === 'object') {
          setHighRisk(fs.highRisk);
        }
        setDraftAttachments(data.data?.attachments || []);
        setDraftToken(token);
        setIsResubmission(!!data.data?.resubmission);
        setSentBack(data.data?.sentBack || null);
      } catch (err: any) {
        if (cancelled) return;
        setDraftHydrateError(err?.message || 'Could not load saved draft');
      } finally {
        if (!cancelled) setDraftHydrating(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiBase]);

  // Restore locally-autosaved answers on mount (fresh application only). This
  // makes a page refresh or a dropped save non-destructive: whatever the vendor
  // typed is recovered from localStorage even though files must be re-attached.
  useEffect(() => {
    if (!autosaveMode) {
      persistReadyRef.current = true;
      return;
    }
    try {
      const raw = localStorage.getItem(AUTOSAVE_KEY);
      if (raw) {
        const saved = JSON.parse(raw);
        const fresh =
          saved && typeof saved.savedAt === 'number'
            ? Date.now() - saved.savedAt < AUTOSAVE_TTL_MS
            : false;
        if (fresh && saved.state && typeof saved.state === 'object') {
          setState(prev => ({ ...prev, ...saved.state, ...sanitiseRestoredBanking(saved.state) }));
          if (saved.highRisk && typeof saved.highRisk === 'object') {
            setHighRisk(saved.highRisk);
          }
          if (typeof saved.step === 'number') setStep(saved.step);
          setRestoredFromLocal(true);
          // Re-attach to the server draft this browser saved, so its uploaded
          // files come back with the answers. Without this, a vendor who saved,
          // closed the tab and came back through the plain link got their typed
          // answers but none of their files, and their next save started a NEW
          // draft — Larrakia worked through nine of them and submitted the one
          // without its HSE documents (Oct 2026). The local answers are kept as
          // restored: they are at least as recent as the server copy. An expired
          // or removed draft is simply let go.
          if (typeof saved.draftToken === 'string' && saved.draftToken) {
            const token = saved.draftToken;
            (async () => {
              try {
                const res = await fetch(`${apiBase}/api/vendor-prequal/draft/${encodeURIComponent(token)}`);
                const data = await res.json();
                if (!res.ok || !data.success) return;
                const atts = data.data?.attachments || [];
                // If the vendor already saved a NEW draft before this answered,
                // keep that one rather than switching them back.
                if (draftTokenRef.current) return;
                setDraftToken(token);
                setDraftAttachments(atts);
                setRestoredDraftFiles(atts.length);
              } catch (_e) {
                // Offline or gone — the answers are restored either way.
              }
            })();
          }
        } else if (!fresh) {
          localStorage.removeItem(AUTOSAVE_KEY);
        }
      }
    } catch (_e) {
      // Corrupt payload / storage disabled — ignore and start clean.
    }
    persistReadyRef.current = true;
  }, [autosaveMode, apiBase]);

  // Debounced autosave of typed answers to localStorage.
  useEffect(() => {
    if (!autosaveMode || !persistReadyRef.current) return undefined;
    const timer = setTimeout(() => {
      try {
        localStorage.setItem(
          AUTOSAVE_KEY,
          JSON.stringify({ v: 1, savedAt: Date.now(), state, highRisk, step, draftToken })
        );
      } catch (_e) {
        // Storage full / private mode — non-fatal, the form still works.
      }
    }, 600);
    return () => clearTimeout(timer);
  }, [autosaveMode, state, highRisk, step, draftToken]);

  usePublicPageScroll();

  const steps = useMemo<string[]>(() => {
    const base = ['Type', 'Company Profile', 'Commercial', 'Corporate Responsibility'];
    if (state.prequalificationType === 'High-risk') {
      return [...base, 'HSE', 'Quality', 'IR', 'Review'];
    }
    return [...base, 'Review'];
  }, [state.prequalificationType]);

  // Clear the validation highlight on a field as soon as the vendor edits it.
  function clearFieldError(field: string) {
    setFieldErrors(prev => {
      if (!prev.has(field)) return prev;
      const next = new Set(prev);
      next.delete(field);
      return next;
    });
  }

  function updateField<K extends keyof FormState>(key: K, value: FormState[K]) {
    clearFieldError(String(key));
    setState(s => ({ ...s, [key]: value }));
  }

  function updateNested(path: string[], value: any) {
    clearFieldError(path.join('.'));
    setState(s => {
      const next: any = structuredClone(s);
      let ref = next;
      for (let i = 0; i < path.length - 1; i++) ref = ref[path[i]];
      ref[path[path.length - 1]] = value;
      return next;
    });
  }

  function onFileChange(fieldKey: string, selected: FileList | null, multiple = false) {
    if (!selected) return;
    const arr = Array.from(selected);
    setFiles(f => ({ ...f, [fieldKey]: multiple ? [...(f[fieldKey] || []), ...arr] : arr }));
  }

  function removeFile(fieldKey: string, idx: number) {
    setFiles(f => ({ ...f, [fieldKey]: (f[fieldKey] || []).filter((_, i) => i !== idx) }));
  }

  /**
   * Dev helper — auto-fill every field with realistic dummy data and attach a
   * single dummy PDF to each upload slot. Only exposed in dev builds.
   */
  function fillTestData() {
    const isHighRisk = state.prequalificationType === 'High-risk';
    // A year out, so the demo data exercises the "insurance does not cap the
    // 3-year qualification term" path rather than tripping the expiry guard.
    const demoInsuranceExpiry = new Date(Date.now() + 365 * 86_400_000)
      .toISOString()
      .slice(0, 10);

    setState({
      prequalificationType: state.prequalificationType || 'Low-risk',
      grpsContactName: 'Rikki Nye',
      company: {
        legalName: "The Trustee for Acme Family Trust",
        tradingName: 'Acme Industrial Services',
        yearFounded: '2010',
        phoneNumber: '07 4662 4448',
        abn: '48 001 588 126',
        websiteUrl: 'www.acmeindustrial.com.au',
      },
      headOffice: {
        streetAddress: '29 Hospital Road',
        suburb: 'Dalby',
        postcode: '4405',
        state: 'QLD',
        country: 'Australia',
      },
      designatedContact: {
        name: 'Anthony Alexander',
        jobTitle: 'Owner / Operator',
        email: 'services@acmeindustrial.com.au',
        contactNumber: '0438 187 191',
      },
      designatedContacts: [
        {
          name: 'Anthony Alexander',
          jobTitle: 'Owner / Operator',
          email: 'services@acmeindustrial.com.au',
          contactNumber: '0438 187 191',
          isPrimary: true,
        },
        {
          name: 'Mel Alexander',
          jobTitle: 'Operations',
          email: 'ops@acmeindustrial.com.au',
          contactNumber: '0438 187 192',
          isPrimary: false,
        },
      ],
      contractsSalesContact: {
        name: 'Anthony Alexander',
        jobTitle: 'Owner / Operator',
        email: 'services@acmeindustrial.com.au',
        contactNumber: '07 4662 4448',
      },
      accountsRemittanceContact: {
        name: 'Mel Alexander',
        jobTitle: 'Accounts',
        email: 'accounts@acmeindustrial.com.au',
      },
      additionalQuestions: {
        registeredForGst: true,
        providesHazardousMaterials: true,
        hasReconciliationActionPlan: false,
        qualifiesAsIndigenousBusiness: false,
        supplyNationStatus: 'N/A',
        productsServices: ['Inspection, Testing, Certification, Calibration', 'Other tools'],
        employeeCount: '4',
        currentlyApprovedSupplier: false,
        yearsSupplyingToGrps: '2',
      },
      terms: {
        hasActiveLitigation: false,
        litigationDetails: '',
        purchaseOrderTermsAccepted: true,
      purchaseOrderTermsStatus: 'accepted',
      purchaseOrderTermsDeviationNotes: '',
      serviceAgreementAccepted: false,
      },
      insurances: {
        generalThirdPartyLiability: {
          ...emptyInsurance(),
          meetsMinimum: true,
          limitsText: 'Per Occurrence $20,000,000',
          expiryDate: demoInsuranceExpiry,
          insurer: 'Marsh Advantage',
          policyNumber: 'PL-1234567',
        },
        employersLiability: {
          ...emptyInsurance(),
          meetsMinimum: true,
          limitsText:
            'Comply with State & Federal Laws. Inc. Extended Cover for Offshore Work (if relevant).',
          expiryDate: demoInsuranceExpiry,
          insurer: 'WorkCover QLD',
          policyNumber: 'WC-7654321',
        },
        professionalIndemnity: {
          ...emptyInsurance(),
          meetsMinimum: true,
          limitsText: 'Per Occurrence $20,000,000',
          expiryDate: demoInsuranceExpiry,
          insurer: 'Marsh Advantage',
          policyNumber: 'PI-2468101',
        },
        professionalIndemnitySoleTrader: {
          ...emptyInsurance(),
          limitsText: 'Per Occurrence $10,000,000',
        },
        motorVehicle: {
          ...emptyInsurance(),
          meetsMinimum: true,
          limitsText: 'Per Occurrence $10,000,000',
          expiryDate: demoInsuranceExpiry,
        },
        propertyEquipment: { ...emptyInsurance(), limitsText: 'Per incident Market Value' },
      },
      additionalInsuranceDetails:
        'All insurances renewed annually on 1 January. Broker: Marsh Advantage.',
      banking: {
        accountName: 'Acme Industrial Services',
        bank: 'ANZ',
        bsb: '014570',
        accountNumber: '322885729',
        countryOfAccount: 'Australia',
        institutionNumber: '',
        transitNumber: '',
        swift: '',
        iban: '',
      },
      experience: [
        {
          contractProject: 'Regional Pest Control',
          client: 'Western Downs Regional Council',
          goodsServices: 'General Pest Control and Termite Management',
          timeline: '7 years',
          contractValue: '$85,000',
        },
      ],
      subSubcontractors: { sublets: false, details: '' },
      corporateResponsibilityAcceptances: new Array(8).fill(true),
    });

    // Build a dummy PDF attachment
    const dummyBlob = new Blob(
      ['%PDF-1.4\n%% GRPS Vendor Prequal — TEST FILL ME dummy attachment\n'],
      { type: 'application/pdf' }
    );
    const dummyFile = new File([dummyBlob], 'test-dummy.pdf', { type: 'application/pdf' });

    // Collect every upload fieldKey we know about
    const uploadKeys = new Set<string>([
      'isoLicences',
      ...INSURANCE_TYPES.map(i => `insurance.${i.key}`),
    ]);

    // High-risk schema upload keys
    if (isHighRisk) {
      for (const part of HIGH_RISK_PARTS) {
        for (const section of part.sections) {
          for (const group of section.groups) {
            for (const field of group.fields) {
              if (field.type === 'upload') uploadKeys.add(field.key);
              const uk = (field as any).uploadKey;
              if (uk) uploadKeys.add(uk);
            }
          }
        }
      }
    }

    const fileMap: FileMap = {};
    uploadKeys.forEach(k => {
      fileMap[k] = [dummyFile];
    });
    setFiles(fileMap);

    // High-risk answers — fill every yes/no as Yes, every textarea with sample text
    if (isHighRisk) {
      const hr: Record<string, any> = {};
      for (const part of HIGH_RISK_PARTS) {
        for (const section of part.sections) {
          for (const group of section.groups) {
            for (const field of group.fields) {
              if (field.type === 'yesno') {
                hr[field.key] = true;
                if ((field as any).detailsKey) {
                  hr[(field as any).detailsKey] = `Dummy details for: ${field.label}`;
                }
              } else if (field.type === 'textarea') {
                hr[field.key] = `Dummy answer for: ${field.label}`;
              } else if (field.type === 'person') {
                hr[`${field.key}.name`] = 'Anthony Alexander';
                hr[`${field.key}.title`] = 'Owner / Operator';
                hr[`${field.key}.experience`] =
                  '20 years industry experience; Cert IV in WHS, HSE Advisor.';
              }
            }
          }
        }
      }
      setHighRisk(hr);
    }
  }

  function clearLocalAutosave() {
    try {
      localStorage.removeItem(AUTOSAVE_KEY);
    } catch (_e) {
      // ignore — storage may be unavailable
    }
  }

  // "Start over" from the restored-draft banner: wipe the local copy and reset.
  function discardRestoredDraft() {
    clearLocalAutosave();
    setState(initialState);
    setHighRisk({});
    setFiles({});
    setStep(0);
    setRestoredFromLocal(false);
    // Let go of the re-attached server draft too, or the fresh start's first
    // save would write into the old one.
    setDraftToken(null);
    setDraftAttachments([]);
    setRestoredDraftFiles(0);
    setError('');
  }

  // ---------------------------------------------------------------------------
  // Per-step validation. A step must pass before the vendor can move forward
  // (Next button or jumping ahead via the step indicator) and before submit.
  // Each error carries a stable `field` key so the offending control can be
  // highlighted.
  // ---------------------------------------------------------------------------
  // A Certificate of Currency exists for an insurance key if it was uploaded
  // in this session (files) or was carried over from a resumed draft.
  function hasInsuranceCertificate(key: string): boolean {
    const fieldKey = `insurance.${key}`;
    if ((files[fieldKey] || []).length > 0) return true;
    return draftAttachments.some(a => a.fieldKey === fieldKey);
  }

  // Which insurances are mandatory for the selected risk level (drives the
  // "Required" badge + inline error). Property/Vehicle are always optional.
  function isInsuranceRequired(key: string): boolean {
    const t = state.prequalificationType;
    if (t === 'Low-risk' || t === 'High-risk') {
      if (key === 'generalThirdPartyLiability' || key === 'employersLiability') return true;
    }
    if (t === 'High-risk' && (key === 'professionalIndemnity' || key === 'professionalIndemnitySoleTrader')) {
      return true;
    }
    return false;
  }

  /**
   * High-risk sections of one part that the vendor accepted — a Yes on a
   * question offering a document upload — with no document attached anywhere
   * in the section, counting this session's uploads and the saved draft's.
   * Mirrors backend/services/vendorPrequalSectionDocs.ts, which enforces it on
   * submit and explains the rule.
   */
  function undocumentedSections(partKey: 'hse' | 'quality' | 'ir'): Array<{ prefix: string; title: string }> {
    const part = HIGH_RISK_PARTS.find(p => p.key === partKey);
    if (!part) return [];
    const out: Array<{ prefix: string; title: string }> = [];
    for (const section of part.sections) {
      const yesWithUpload = section.groups
        .flatMap(g => g.fields)
        .filter(f => f.type === 'yesno' && !!(f as { uploadKey?: string }).uploadKey);
      if (!yesWithUpload.some(f => highRisk[f.key] === true)) continue;
      const prefix = yesWithUpload[0].key.split('.').slice(0, 2).join('.');
      const documented =
        Object.entries(files).some(([k, list]) => k.startsWith(`${prefix}.`) && list.length > 0) ||
        draftAttachments.some(a => String(a.fieldKey || '').startsWith(`${prefix}.`));
      if (!documented) out.push({ prefix, title: section.title });
    }
    return out;
  }

  function validateStep(stepName: string): { field: string; message: string }[] {
    // Admin edit mode edits an existing (possibly legacy) record — keep its
    // current free navigation and don't gate it.
    if (adminEditId) return [];
    const errors: { field: string; message: string }[] = [];
    const isBasic = state.prequalificationType === 'Basic';
    switch (stepName) {
      case 'Type':
        if (!state.prequalificationType) {
          errors.push({
            field: 'prequalificationType',
            message: 'Please select a prequalification type.',
          });
        }
        if (!state.grpsContactName.trim()) {
          errors.push({
            field: 'grpsContactName',
            message: 'Please enter your GR Production Services contact.',
          });
        }
        break;
      case 'HSE':
      case 'Quality':
      case 'IR':
        if (!isResubmission) {
          const partKey = stepName === 'HSE' ? 'hse' : stepName === 'Quality' ? 'quality' : 'ir';
          for (const s of undocumentedSections(partKey)) {
            errors.push({
              field: `highRisk.${s.prefix}`,
              message: `${s.title}: you answered Yes, so please attach at least one supporting document in this section.`,
            });
          }
        }
        break;
      case 'Company Profile':
        if (!state.company.legalName.trim()) {
          errors.push({
            field: 'company.legalName',
            message: 'Please enter your company legal name.',
          });
        }
        // GreenTree's limits, enforced on the step rather than discovered at
        // submit after every page is filled in. The server re-checks on submit.
        for (const k of ['legalName', 'tradingName'] as const) {
          if (String(state.company[k] || '').trim().length > GT_ENTITY_NAME_MAX) {
            errors.push({
              field: `company.${k}`,
              message: `${k === 'legalName' ? 'Legal name' : 'Trading name'} must be ${GT_ENTITY_NAME_MAX} characters or fewer — GreenTree cannot store a longer one.`,
            });
          }
        }
        // Designated contact is required for ALL prequalification types (this
        // is what removes "anonymous" submissions) — every contact must have
        // all fields, and exactly one must be marked primary.
        {
          const contacts = state.designatedContacts || [];
          if (contacts.length === 0) {
            errors.push({
              field: 'designatedContacts',
              message: 'At least one designated contact is required.',
            });
          } else {
            const allComplete = contacts.every(
              c =>
                c.name.trim() &&
                c.jobTitle.trim() &&
                /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((c.email || '').trim()) &&
                c.contactNumber.trim()
            );
            if (!allComplete) {
              errors.push({
                field: 'designatedContacts',
                message:
                  'Every designated contact must have a name, job title, valid email and contact number.',
              });
            }
            if (!contacts.some(c => c.isPrimary)) {
              errors.push({
                field: 'designatedContacts',
                message: 'Please mark one designated contact as primary.',
              });
            }
          }
        }
        break;
      case 'Commercial':
        // GreenTree holds at most 20 characters for these two insurer names and
        // refuses the WHOLE supplier record over one (Catapult, Sep 2026).
        for (const k of ['generalThirdPartyLiability', 'employersLiability'] as const) {
          if (String(state.insurances[k]?.insurer || '').trim().length > GT_INSURER_NAME_MAX) {
            errors.push({
              field: `insurances.${k}.insurer`,
              message: `Insurer name must be ${GT_INSURER_NAME_MAX} characters or fewer — GreenTree cannot store a longer one. Please use a short form, e.g. "QBE Australia".`,
            });
          }
          if (String(state.insurances[k]?.policyNumber || '').trim().length > GT_POLICY_NUMBER_MAX) {
            errors.push({
              field: `insurances.${k}.policyNumber`,
              message: `Policy number must be ${GT_POLICY_NUMBER_MAX} characters or fewer.`,
            });
          }
        }
        // Asked of every type, Basic included, from 2026-09-17.
        // A High-risk vendor who accepted the GRPS Service Agreement is not asked
        // the PO terms (the server normalises the answer; vendorPrequalTerms.ts).
        if (
          state.terms.purchaseOrderTermsStatus === '' &&
          !(state.prequalificationType === 'High-risk' && state.terms.serviceAgreementAccepted)
        ) {
          errors.push({
            field: 'terms.purchaseOrderTermsStatus',
            message:
              'Please select whether you accept the Standard Purchase Order Terms and Conditions, or require a deviation.',
          });
        }
        // Banking details are required for every type, including Basic.
        {
          if (!state.banking.accountName.trim()) {
            errors.push({
              field: 'banking.accountName',
              message: 'Please enter the bank account name.',
            });
          }
          if (!state.banking.accountNumber.trim()) {
            errors.push({
              field: 'banking.accountNumber',
              message: 'Please enter the bank account number.',
            });
          }
          const au = isAustralianAccount(
            state.banking.countryOfAccount,
            state.headOffice.country
          );
          if (au && state.banking.bsb.length !== 6) {
            errors.push({
              field: 'banking.bsb',
              message: 'Please enter a valid 6-digit BSB (digits only).',
            });
          }
          // Account number rules are country-aware, and mirror
          // backend/utils/bankAccountValidation.ts — which is the enforcement
          // point, since a restored autosave, an autofill or a direct API call
          // can seat a value without passing through onChange.
          //
          // Australian: digits only, 6-9 of them. That is the shape GreenTree
          // actually stores (profiled over 1,180 supplier records: 6-9 chars,
          // none containing a letter). Anything else was being silently stripped
          // to its digits on the way to GreenTree and posted as a wrong account
          // number. This is NOT the old blanket 9-digit cap coming back — SWIFT
          // and IBAN now have their own fields below, so they no longer need to
          // be tolerated here.
          const acct = state.banking.accountNumber.trim();
          if (acct && au) {
            if (!/^[0-9]+$/.test(acct)) {
              errors.push({
                field: 'banking.accountNumber',
                message:
                  'An Australian bank account number is digits only. For an overseas account, set the Country of Bank Account and use the SWIFT/IBAN fields.',
              });
            } else if (acct.length < 6 || acct.length > 9) {
              errors.push({
                field: 'banking.accountNumber',
                message: 'An Australian bank account number is between 6 and 9 digits.',
              });
            }
          }
          // Overseas: alphanumeric, no length ceiling (the 2026 decision).
          if (acct && !au && !/^[A-Za-z0-9]+$/.test(acct)) {
            errors.push({
              field: 'banking.accountNumber',
              message:
                'Please enter a valid account number — letters and numbers only, with no spaces, dashes or other special characters.',
            });
          }
          if (!au) {
            const swift = state.banking.swift.trim();
            if (swift && !/^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(swift)) {
              errors.push({
                field: 'banking.swift',
                message: 'A SWIFT/BIC code is 8 or 11 characters, e.g. CTBAAU2S.',
              });
            }
            const iban = state.banking.iban.trim();
            if (iban && !/^[A-Z]{2}[0-9]{2}[A-Z0-9]{11,30}$/.test(iban)) {
              errors.push({
                field: 'banking.iban',
                message:
                  'Please check the IBAN — it starts with a 2-letter country code and 2 digits.',
              });
            }
          }
        }
        // Insurance by risk level: Low/High-risk require public liability +
        // workers compensation; High-risk additionally requires professional
        // indemnity (standard or sole-trader). Each required cover must be
        // confirmed as meeting the minimum AND have a Certificate of Currency.
        if (state.prequalificationType === 'Low-risk' || state.prequalificationType === 'High-risk') {
          const insOk = (key: (typeof INSURANCE_TYPES)[number]['key']) =>
            !!state.insurances[key]?.meetsMinimum && hasInsuranceCertificate(key);

          // The expiry date is only required where the vendor has ALREADY
          // asserted the cover — i.e. on a policy the form is gating anyway. It
          // never turns an otherwise-complete form into a blocked one, which
          // matters: the last mandatory field added here stalled 12 drafts.
          INSURANCE_TYPES.forEach(({ key }) => {
            if (state.insurances[key]?.meetsMinimum && !state.insurances[key]?.expiryDate) {
              errors.push({
                field: `insurance.${key}.expiryDate`,
                message:
                  'Please give the policy expiry date shown on the Certificate of Currency.',
              });
            }
          });
          if (!insOk('generalThirdPartyLiability')) {
            errors.push({
              field: 'insurance.generalThirdPartyLiability',
              message:
                'Public Liability insurance is required — confirm it meets the minimum and attach a Certificate of Currency.',
            });
          }
          if (!insOk('employersLiability')) {
            errors.push({
              field: 'insurance.employersLiability',
              message:
                "Workers Compensation / Employer's Liability is required — confirm the minimum and attach a Certificate of Currency.",
            });
          }
          if (
            state.prequalificationType === 'High-risk' &&
            !insOk('professionalIndemnity') &&
            !insOk('professionalIndemnitySoleTrader')
          ) {
            errors.push({
              field: 'insurance.professionalIndemnity',
              message:
                'Professional Indemnity is required for High-risk — confirm the minimum and attach a Certificate of Currency (standard or sole-trader).',
            });
          }
        }
        break;
      case 'Corporate Responsibility':
        // Basic: the public liability question must be answered, and a Yes
        // needs its certificate. A No is a complete answer (procurement
        // decides). New submissions only, like the server's
        // basicPublicLiabilityError (backend/services/vendorPrequalSectionDocs.ts).
        if (isBasic && !isResubmission) {
          const held = state.insurances.generalThirdPartyLiability?.held;
          if (held !== true && held !== false) {
            errors.push({
              field: 'insurances.generalThirdPartyLiability.held',
              message: 'Please tell us whether your business holds current Public Liability insurance.',
            });
          } else if (held && !hasInsuranceCertificate('generalThirdPartyLiability')) {
            errors.push({
              field: 'insurance.generalThirdPartyLiability',
              message: 'Please attach a current Certificate of Currency for your Public Liability insurance.',
            });
          }
        }
        if (
          !isBasic &&
          !(
            state.corporateResponsibilityAcceptances.length === 8 &&
            state.corporateResponsibilityAcceptances.every(Boolean)
          )
        ) {
          errors.push({
            field: 'corporateResponsibilityAcceptances',
            message:
              'Please tick every corporate responsibility statement to acknowledge and agree.',
          });
        }
        break;
      default:
        break;
    }
    return errors;
  }

  /**
   * Navigate to `target`. Backward moves are always allowed. Forward moves
   * validate every step between the current position and the target; the first
   * incomplete step blocks the move, surfaces its errors and lands the vendor
   * on it so they can fix it.
   */
  function goToStep(target: number) {
    const clamped = Math.max(0, Math.min(steps.length - 1, target));
    if (clamped <= step) {
      setError('');
      setFieldErrors(new Set());
      setStep(clamped);
      return;
    }
    for (let i = step; i < clamped; i++) {
      const errs = validateStep(steps[i]);
      if (errs.length) {
        setFieldErrors(new Set(errs.map(e => e.field)));
        setError(errs.map(e => e.message).join(' '));
        setStep(i);
        return;
      }
    }
    setError('');
    setFieldErrors(new Set());
    setStep(clamped);
  }

  function buildPayloadForSave() {
    const payload: any = structuredClone(state);
    // Mirror the primary designated contact into the legacy single field so
    // the GreenTree payload + older readers stay correct (backend also enforces
    // this in a pre-save hook).
    const primary = primaryOf(state.designatedContacts);
    if (primary) {
      payload.designatedContact = {
        name: primary.name,
        jobTitle: primary.jobTitle,
        email: primary.email,
        contactNumber: primary.contactNumber,
      };
    }
    payload.company.yearFounded = state.company.yearFounded
      ? Number(state.company.yearFounded)
      : undefined;
    payload.additionalQuestions.employeeCount = state.additionalQuestions.employeeCount
      ? Number(state.additionalQuestions.employeeCount)
      : 0;
    INSURANCE_TYPES.forEach(({ key, minimum }) => {
      payload.insurances[key].limitsText = minimum;
    });
    if (state.prequalificationType === 'High-risk') {
      payload.highRisk = highRisk;
    }
    return payload;
  }

  async function onSubmit() {
    // Final gate — re-validate every step (all but the Review step itself) so
    // required fields and the agreement acceptances can't be bypassed by
    // jumping straight to Review. Land the vendor on the first incomplete step.
    for (let i = 0; i < steps.length - 1; i++) {
      const errs = validateStep(steps[i]);
      if (errs.length) {
        setFieldErrors(new Set(errs.map(e => e.field)));
        setError(errs.map(e => e.message).join(' '));
        setStep(i);
        return;
      }
    }
    setError('');
    setFieldErrors(new Set());
    setSubmitting(true);
    try {
      const fd = new FormData();
      const payload = buildPayloadForSave();
      fd.append('submission', JSON.stringify(payload));

      Object.entries(files).forEach(([fieldKey, fs]) => {
        fs.forEach(f => fd.append(fieldKey, f, f.name));
      });
      // Saved files the vendor removed since their last save — without this a
      // removed file was still carried into the submission.
      if (removedDraftAttIds.length) {
        fd.append('removeAttachmentIds', JSON.stringify(removedDraftAttIds));
      }

      if (adminEditId) {
        // Admin edit mode → PUT /:id with JWT
        const token = getAuthToken() || '';
        const headers: Record<string, string> = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;
        const res = await fetchWithTimeout(
          `${apiBase}/api/vendor-prequal/${encodeURIComponent(adminEditId)}`,
          { method: 'PUT', body: fd, headers }
        );
        const data = await res.json();
        if (!res.ok || !data.success) {
          throw new Error(data.error || `Save failed (HTTP ${res.status})`);
        }
        setAdminEditSaved(true);
        return;
      }

      // Public submit path (with optional draftToken)
      fd.append('website_trap', ''); // honeypot
      if (draftToken) fd.append('draftToken', draftToken);

      const res = await fetchWithTimeout(`${apiBase}/api/vendor-prequal/submit`, {
        method: 'POST',
        body: fd,
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || `Submit failed (HTTP ${res.status})`);
      }
      // Submission is safely on the server now — the local copy is no longer
      // needed and would otherwise resurface on the next visit.
      clearLocalAutosave();
      setSubmittedId(data.id);
    } catch (err: any) {
      setError(describeSaveError(err, autosaveMode));
    } finally {
      setSubmitting(false);
    }
  }

  /**
   * Save & Close. If no email is in the form yet, opens the prompt modal so
   * the vendor can supply one — we need somewhere to send the resume link.
   */
  function onSaveAndCloseClick() {
    setError('');
    const formEmail = (primaryOf(state.designatedContacts)?.email || '').trim();
    if (formEmail && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formEmail)) {
      void runSaveDraft(formEmail);
      return;
    }
    setSaveModalEmail(formEmail);
    setShowSaveModal(true);
  }

  async function runSaveDraft(email: string) {
    setShowSaveModal(false);
    setSavingDraft(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('submission', JSON.stringify(buildPayloadForSave()));
      fd.append('email', email);
      if (removedDraftAttIds.length) {
        fd.append('removeAttachmentIds', JSON.stringify(removedDraftAttIds));
      }
      Object.entries(files).forEach(([fieldKey, fs]) => {
        fs.forEach(f => fd.append(fieldKey, f, f.name));
      });

      const url = draftToken
        ? `${apiBase}/api/vendor-prequal/draft/${encodeURIComponent(draftToken)}`
        : `${apiBase}/api/vendor-prequal/draft`;
      const res = await fetchWithTimeout(url, {
        method: draftToken ? 'PUT' : 'POST',
        body: fd,
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || `Save failed (HTTP ${res.status})`);
      }

      // After a successful save the server now owns those files — reset the
      // in-session uploads and pending-removals so subsequent saves don't
      // re-send them.
      setFiles({});
      setRemovedDraftAttIds([]);
      // Refresh draft attachments list so the "saved files" UI is accurate.
      try {
        const r2 = await fetch(
          `${apiBase}/api/vendor-prequal/draft/${encodeURIComponent(data.token)}`
        );
        const d2 = await r2.json();
        if (r2.ok && d2.success) setDraftAttachments(d2.data?.attachments || []);
      } catch (_e) {
        // ignore — non-fatal
      }
      if (!draftToken) setDraftToken(data.token);
      setSaveResult({
        link: data.link,
        sentTo: email,
        emailSent: !!data.emailSent,
      });
    } catch (err: any) {
      setError(describeSaveError(err, autosaveMode));
    } finally {
      setSavingDraft(false);
    }
  }

  function removeDraftAttachment(attId: string) {
    setDraftAttachments(list => list.filter(a => a._id !== attId));
    setRemovedDraftAttIds(ids => (ids.includes(attId) ? ids : [...ids, attId]));
  }

  // --- Render success state ---
  if (submittedId) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-xl bg-white p-8 rounded-lg shadow-sm">
          <h1 className="text-2xl font-semibold text-green-700">Thank you</h1>
          <p className="mt-4 text-gray-700">
            Your Vendor Prequalification submission has been received and is now under review by
            the GRPS Procurement team. You will be contacted if further information is required or
            once a decision has been made.
          </p>
          <p className={`mt-4 ${TEXT.helper}`}>
            Reference ID: <code>{submittedId}</code>
          </p>
        </div>
      </div>
    );
  }

  if (adminEditSaved) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-xl bg-white p-8 rounded-lg shadow-sm">
          <h1 className="text-2xl font-semibold text-green-700">Changes saved</h1>
          <p className="mt-4 text-gray-700">
            This submission has been updated. You can close this tab and return to the
            Vendor Prequalification admin view.
          </p>
          <Button variant="primary" onClick={() => window.close()} className="mt-4">
            Close tab
          </Button>
        </div>
      </div>
    );
  }

  // --- Step renderers ---
  const renderTypeStep = () => (
    <>
      <Section title="Vendor Prequalification">
        <div className={`space-y-3 leading-relaxed ${TEXT.body}`}>
          <p>
            Vendors that supply goods or services to GR Production Services are required to
            complete this Prequalification questionnaire, which upon approval is valid for{' '}
            <strong>3 years</strong>.
          </p>
          <p>Completion of this prequalification does not constitute an agreement to transact business.</p>
          <p>
            To maintain an &lsquo;approved&rsquo; status, up-to-date certificates, licences and
            insurances must be provided to GR Production Services. As part of ongoing management,
            GR Production Services may conduct inspections and audits of the Vendors practices,
            without notice on project sites, and with notice at the Vendors premises.
          </p>
          <p>
            The Vendors must maintain records that relate to supply and compliance (audit,
            inspection, log books, receipts, etc.) for a minimum of seven years after the work is
            performed, or as legally required. Such records must be made available to GR
            Production Services on request.
          </p>
          <p>
            Please select the prequalification type from the following options as directed by your
            GR Production Services contact; if unsure, please contact{' '}
            <a className="text-blue-700 underline" href="mailto:procurement@grps.com.au">
              procurement@grps.com.au
            </a>
            .
          </p>
        </div>
      </Section>

      <Section title="Prequalification Type">
        <div className="space-y-4">
          {/* Basic */}
          <label
            className={`flex items-start gap-3 p-4 border rounded-lg cursor-pointer ${
              state.prequalificationType === 'Basic'
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200'
            }`}
          >
            <input
              type="radio"
              checked={state.prequalificationType === 'Basic'}
              onChange={() => updateField('prequalificationType', 'Basic')}
              className="mt-1"
            />
            <div className="flex-1">
              <div className="font-semibold text-blue-900">Basic Prequalification</div>
              <div className={`mt-1 ${TEXT.bodyMuted}`}>
                To be used only for ongoing vendors which fulfill one of the following criteria
                and cannot be paid for by company credit card:
              </div>
              <ul className={`mt-1 list-disc pl-5 ${TEXT.bodyMuted}`}>
                <li>Are a charity, government department or utilities provider; or</li>
                <li>
                  Are a training organisation that provide courses not part of the Australian
                  Qualifications Framework (e.g. cultural awareness training) and are conducted on
                  GR Production Services (GRPS) sites; or
                </li>
                <li>
                  Are a retailer of off-the-shelf low-risk products or services to the general
                  public and monthly spend will be less than $5,000.
                </li>
              </ul>
              <div className={`mt-2 ${TEXT.labelMuted}`}>Examples:</div>
              <ul className={`list-disc pl-5 ${TEXT.bodyMuted}`}>
                <li>Passenger vehicle service centres (e.g. Toyota dealership)</li>
                <li>Merchandise suppliers of promotional material</li>
                <li>Hotels or small local stores that cannot complete the &lsquo;low-risk&rsquo; prequal</li>
              </ul>
            </div>
          </label>

          {/* Low-risk */}
          <label
            className={`flex items-start gap-3 p-4 border rounded-lg cursor-pointer ${
              state.prequalificationType === 'Low-risk'
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200'
            }`}
          >
            <input
              type="radio"
              checked={state.prequalificationType === 'Low-risk'}
              onChange={() => updateField('prequalificationType', 'Low-risk')}
              className="mt-1"
            />
            <div className="flex-1">
              <div className="font-semibold text-blue-900">Low-risk Prequalification</div>
              <div className={`mt-1 ${TEXT.bodyMuted}`}>
                A Supplier or Service Provider who provides general industrial goods or services
                to GR Production Services and our clients (not performing work onsite), including:
              </div>
              <ul className={`mt-1 list-disc pl-5 ${TEXT.bodyMuted}`}>
                <li>Engineering, consultancy and survey services</li>
                <li>Inspection, testing and certification services / Machining and fabrication services</li>
                <li>Spare / replacement part supply or new asset supply</li>
                <li>Rental equipment supply</li>
              </ul>
            </div>
          </label>

          {/* High-risk */}
          <label
            className={`flex items-start gap-3 p-4 border rounded-lg cursor-pointer ${
              state.prequalificationType === 'High-risk'
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200'
            }`}
          >
            <input
              type="radio"
              checked={state.prequalificationType === 'High-risk'}
              onChange={() => updateField('prequalificationType', 'High-risk')}
              className="mt-1"
            />
            <div className="flex-1">
              <div className="font-semibold text-blue-900">High-risk Prequalification</div>
              <div className={`mt-1 ${TEXT.bodyMuted}`}>
                A Subcontractor who is responsible to perform work using their own labour and
                equipment on GR Production Services or its Client sites/premises, or fabrication
                of custom-made goods over AUD$100k value. For example:
              </div>
              <ul className={`mt-1 list-disc pl-5 ${TEXT.bodyMuted}`}>
                <li>A blasting and painting subcontractor engaged to work on a client site onshore/offshore</li>
                <li>Fabrication of custom-made testing machinery &amp; technology with value over AUD$100k</li>
              </ul>
              <div className={`mt-2 ${TEXT.bodyMuted}`}>
                This type of prequalification requires a full review by GR Production Services&rsquo;
                HSE, Quality and Industrial Relations (HR) departments and may require further
                meetings and site visits prior to approval.
              </div>
            </div>
          </label>
        </div>
        <FieldError show={fieldErrors.has('prequalificationType')}>
          Please select a prequalification type.
        </FieldError>

        <div className="mt-6">
          <Label required>Who is your contact at GR Production Services?</Label>
          <TextInput
            value={state.grpsContactName}
            onChange={v => updateField('grpsContactName', v)}
            invalid={fieldErrors.has('grpsContactName')}
          />
          <FieldError show={fieldErrors.has('grpsContactName')}>
            Please enter your GR Production Services contact.
          </FieldError>
        </div>
      </Section>
    </>
  );

/**
 * GreenTree's limit on a field, shown as the vendor approaches it.
 *
 * These are GT's, not ours, and they are small: an insurer name is capped at
 * 20 characters and a legal entity name at 50. A vendor who only finds out on
 * submit has already filled in the whole form, and before this the value was
 * quietly cut to fit instead — which is how two approved suppliers came to sit
 * in GreenTree with a legal entity name severed mid-word.
 *
 * The server owns the rule (`gtSupplierFieldLimits.ts`); this is the hint, and
 * `gtSupplierFieldLimits.test.ts` reads these constants back out of this file
 * so the two cannot drift.
 */
/** Shown above the HSE, Quality and IR steps — the rule is in vendorPrequalSectionDocs.ts. */
const SECTION_DOCUMENTS_NOTE = (
  <p className={`mb-3 bg-blue-50 border border-blue-200 rounded-lg p-3 ${TEXT.body}`}>
    For each section you answer <strong>Yes</strong> to, please attach at least one supporting
    document in that section. The same document can be attached to more than one section.
  </p>
);

const GT_INSURER_NAME_MAX = 20;
const GT_ENTITY_NAME_MAX = 50;
/** Our cap, not a GreenTree statement — GT has never named one (gtSupplierFieldLimits.ts). */
const GT_POLICY_NUMBER_MAX = 50;

function GtLengthHint({ value, max }: { value?: string; max: number }) {
  // Coerced, not assumed. An insurance entry restored from a saved draft can
  // arrive without `insurer` at all, and a hint must never be the reason a
  // form fails to render.
  const n = String(value ?? '').trim().length;
  if (n <= max - 5) return null;
  return (
    <p className={`mt-1 text-xs ${n > max ? 'font-medium text-red-600' : 'text-gray-500'}`}>
      {n}/{max} characters
      {n > max && ' — GreenTree cannot store a value this long. Please shorten it.'}
    </p>
  );
}

  const renderCompanyStep = () => (
    <>
      <Section title="Part 1 — Company Profile: Entity Details">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label required>Legal Name</Label>
            <TextInput
              value={state.company.legalName}
              onChange={v => updateNested(['company', 'legalName'], v)}
              invalid={fieldErrors.has('company.legalName')}
            />
            <GtLengthHint value={state.company.legalName} max={GT_ENTITY_NAME_MAX} />
            <FieldError show={fieldErrors.has('company.legalName')}>
              Please enter your company legal name.
            </FieldError>
          </div>
          <div>
            <Label>Trading Name</Label>
            <TextInput
              value={state.company.tradingName}
              onChange={v => updateNested(['company', 'tradingName'], v)}
            />
            <GtLengthHint value={state.company.tradingName} max={GT_ENTITY_NAME_MAX} />
          </div>
          {state.prequalificationType !== 'Basic' && (
            <div>
              <Label>Year Company Founded</Label>
              <TextInput
                type="number"
                value={state.company.yearFounded}
                onChange={v => updateNested(['company', 'yearFounded'], v)}
              />
            </div>
          )}
          <div>
            <Label>Company Phone Number</Label>
            <TextInput
              value={state.company.phoneNumber}
              onChange={v => updateNested(['company', 'phoneNumber'], v)}
            />
          </div>
          <div>
            <Label>ABN</Label>
            <TextInput
              value={state.company.abn}
              onChange={v => updateNested(['company', 'abn'], v)}
            />
          </div>
          <div>
            <Label>Website URL</Label>
            <TextInput
              value={state.company.websiteUrl}
              onChange={v => updateNested(['company', 'websiteUrl'], v)}
            />
          </div>
        </div>
      </Section>

      <Section title="Head Office Address">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Label>Street Address</Label>
            <TextInput
              value={state.headOffice.streetAddress}
              onChange={v => updateNested(['headOffice', 'streetAddress'], v)}
            />
          </div>
          <div>
            <Label>Suburb</Label>
            <TextInput
              value={state.headOffice.suburb}
              onChange={v => updateNested(['headOffice', 'suburb'], v)}
            />
          </div>
          <div>
            <Label>Postcode</Label>
            <TextInput
              value={state.headOffice.postcode}
              onChange={v => updateNested(['headOffice', 'postcode'], v)}
            />
          </div>
          <div>
            <Label>State</Label>
            <Select
              variant="compact"
              placeholder="Select…"
              className="w-full"
              value={state.headOffice.state}
              onChange={nextValue => updateNested(['headOffice', 'state'], nextValue)}
              options={AUS_STATES.map(s => ({ value: s, label: s }))}
            />
          </div>
          <div>
            <Label>Country</Label>
            <TextInput
              value={state.headOffice.country}
              onChange={v => updateNested(['headOffice', 'country'], v)}
            />
          </div>
        </div>
      </Section>

      <Section title="Designated Contacts">
          <p className={`mb-3 ${TEXT.helper}`}>
            Add one or more designated contacts (required). Mark the main point of contact as
            Primary.
          </p>
            {state.designatedContacts.map((c, idx) => (
              <div key={idx} className="border border-gray-200 rounded-lg p-3 mb-3">
                <div className="flex items-center justify-between mb-2">
                  <label className={`flex items-center gap-2 ${TEXT.bodyMedium}`}>
                    <input
                      type="radio"
                      name="designatedPrimary"
                      checked={c.isPrimary}
                      onChange={() =>
                        updateField(
                          'designatedContacts',
                          state.designatedContacts.map((x, i) => ({ ...x, isPrimary: i === idx }))
                        )
                      }
                    />
                    Primary contact
                  </label>
                  {state.designatedContacts.length > 1 && (
                    <Button variant="linkDanger" size="sm" onClick={() => {
                        const next = state.designatedContacts.filter((_, i) => i !== idx);
                        // guarantee a primary remains
                        if (!next.some(x => x.isPrimary) && next.length) next[0].isPrimary = true;
                        updateField('designatedContacts', next);
                      }}>
                      Remove
                    </Button>
                  )}
                </div>
                {renderContactFields(['designatedContacts', String(idx)], c, true)}
              </div>
            ))}
            <Button variant="link" size="sm" onClick={() =>
                updateField('designatedContacts', [
                  ...state.designatedContacts,
                  emptyDesignatedContact(state.designatedContacts.length === 0),
                ])
              }>
              + Add another contact
            </Button>
      </Section>
      {state.prequalificationType !== 'Basic' && (
        <Section title="Contracts / Sales Contact">
          {renderContactFields(['contractsSalesContact'], state.contractsSalesContact, true)}
        </Section>
      )}

      <Section title="Accounts / Remittance Contact">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Name</Label>
            <TextInput
              value={state.accountsRemittanceContact.name}
              onChange={v => updateNested(['accountsRemittanceContact', 'name'], v)}
            />
          </div>
          <div>
            <Label>Job Title</Label>
            <TextInput
              value={state.accountsRemittanceContact.jobTitle}
              onChange={v => updateNested(['accountsRemittanceContact', 'jobTitle'], v)}
            />
          </div>
          <div className="md:col-span-2">
            <Label>Email</Label>
            <TextInput
              type="email"
              value={state.accountsRemittanceContact.email}
              onChange={v => updateNested(['accountsRemittanceContact', 'email'], v)}
            />
          </div>
        </div>
      </Section>

      <Section title="Additional Questions">
        <div className="space-y-4">
          <div>
            <Label>Is your company registered for GST?</Label>
            <YesNo
              value={state.additionalQuestions.registeredForGst}
              onChange={v => updateNested(['additionalQuestions', 'registeredForGst'], v)}
            />
          </div>
          {state.additionalQuestions.registeredForGst && (
            <div>
              <Label>Years Supplying to GRPS</Label>
              <TextInput
                value={state.additionalQuestions.yearsSupplyingToGrps}
                onChange={v => updateNested(['additionalQuestions', 'yearsSupplyingToGrps'], v)}
              />
            </div>
          )}
          {state.prequalificationType !== 'Basic' && (
            <div>
              <Label>Will your company be providing hazardous materials?</Label>
              <YesNo
                value={state.additionalQuestions.providesHazardousMaterials}
                onChange={v =>
                  updateNested(['additionalQuestions', 'providesHazardousMaterials'], v)
                }
              />
            </div>
          )}
          <div>
            <Label>Do you have a Reconciliation Action Plan or Indigenous Participation Plan?</Label>
            <YesNo
              value={state.additionalQuestions.hasReconciliationActionPlan}
              onChange={v =>
                updateNested(['additionalQuestions', 'hasReconciliationActionPlan'], v)
              }
            />
          </div>
          <div>
            <Label>Does your company qualify as an Indigenous Business (50%+ ownership)?</Label>
            <YesNo
              value={state.additionalQuestions.qualifiesAsIndigenousBusiness}
              onChange={v =>
                updateNested(['additionalQuestions', 'qualifiesAsIndigenousBusiness'], v)
              }
            />
          </div>
          <div>
            <Label>Supply Nation Status</Label>
            <Select
              variant="compact"
              required
              className="w-full"
              value={state.additionalQuestions.supplyNationStatus}
              onChange={nextValue =>
                updateNested(['additionalQuestions', 'supplyNationStatus'], nextValue)
              }
              options={[
                { value: 'Certified', label: 'Certified' },
                { value: 'Registered', label: 'Registered' },
                { value: 'N/A', label: 'N/A' },
              ]}
            />
          </div>
          <div>
            <Label>Products or Services supplied (select all that apply)</Label>
            <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-md p-3 grid grid-cols-1 md:grid-cols-2 gap-1 text-sm">
              {PRODUCTS_SERVICES.map(p => (
                <label key={p} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    className={CHECKBOX_CLASS}
                    checked={state.additionalQuestions.productsServices.includes(p)}
                    onChange={e => {
                      const next = e.target.checked
                        ? [...state.additionalQuestions.productsServices, p]
                        : state.additionalQuestions.productsServices.filter(x => x !== p);
                      updateNested(['additionalQuestions', 'productsServices'], next);
                    }}
                  />
                  {p}
                </label>
              ))}
            </div>
          </div>
          {state.prequalificationType !== 'Basic' && (
            <div>
              <Label>Number of employees</Label>
              <TextInput
                type="number"
                value={state.additionalQuestions.employeeCount}
                onChange={v => updateNested(['additionalQuestions', 'employeeCount'], v)}
              />
            </div>
          )}
          <div>
            <Label>Currently approved supplier to GRPS?</Label>
            <YesNo
              value={state.additionalQuestions.currentlyApprovedSupplier}
              onChange={v =>
                updateNested(['additionalQuestions', 'currentlyApprovedSupplier'], v)
              }
            />
          </div>
        </div>
      </Section>
    </>
  );

  function renderContactFields(path: string[], v: ContactData, includePhone: boolean) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>Name</Label>
          <TextInput value={v.name} onChange={x => updateNested([...path, 'name'], x)} />
        </div>
        <div>
          <Label>Job Title</Label>
          <TextInput value={v.jobTitle} onChange={x => updateNested([...path, 'jobTitle'], x)} />
        </div>
        <div>
          <Label>Email</Label>
          <TextInput
            type="email"
            value={v.email}
            onChange={x => updateNested([...path, 'email'], x)}
          />
        </div>
        {includePhone && (
          <div>
            <Label>Contact Number</Label>
            <TextInput
              value={v.contactNumber}
              onChange={x => updateNested([...path, 'contactNumber'], x)}
            />
          </div>
        )}
      </div>
    );
  }

  // The PO Terms & Conditions answer — Accept or Deviation, a required choice
  // with NO default.
  //
  // A single "I accept in full" tick left a vendor who cannot accept with two
  // options: tick it untruthfully, or abandon. Once acceptance was enforced
  // (7 Jul 2026) the second is what happened. Radios, not a second checkbox, so
  // neither answer is pre-selected and the vendor has to choose deliberately.
  //
  // Extracted so the Basic step and the Low/High-risk Commercial step render the
  // SAME control — Basic vendors were not asked at all until 2026-09-17, and a
  // second copy of this markup is how the two would drift apart again.
  const renderPurchaseOrderTerms = () => (
      <div
        className={`border-t pt-4 ${
          fieldErrors.has('terms.purchaseOrderTermsStatus')
            ? 'border border-red-500 bg-red-50 rounded-lg p-2'
            : ''
        }`}
      >
        <p className="text-sm mb-2">
          GR Production Services{' '}
          <a
            href="https://www.grps.com.au/theme/grpscomau/assets/public/File/00COMAGR001_Purchase_Order_Terms_and_Conditions.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800 underline"
          >
            Standard Purchase Order Terms and Conditions
          </a>{' '}
          (in lieu of a separate Commercial Agreement).{' '}
          <span className="text-red-600">*</span>
        </p>

        <div className="space-y-2">
          {(
            [
              ['accepted', 'We accept the Standard Purchase Order Terms and Conditions in full.'],
              ['deviation', 'We require a deviation to the Standard Purchase Order Terms and Conditions.'],
            ] as Array<[Exclude<PurchaseOrderTermsStatus, ''>, string]>
          ).map(([value, label]) => (
            <label key={value} className="flex items-start gap-2 text-sm">
              <input
                type="radio"
                name="purchaseOrderTermsStatus"
                className="mt-0.5"
                value={value}
                checked={state.terms.purchaseOrderTermsStatus === value}
                onChange={() => {
                  // Keep the legacy boolean in step with the answer so the
                  // two can never disagree. The server derives it again on
                  // submit; this keeps drafts and the progress estimate
                  // honest in the meantime.
                  updateNested(['terms', 'purchaseOrderTermsStatus'], value);
                  updateNested(['terms', 'purchaseOrderTermsAccepted'], value === 'accepted');
                }}
              />
              <span>{label}</span>
            </label>
          ))}
        </div>

        {state.terms.purchaseOrderTermsStatus === 'deviation' && (
          <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
            <p className="text-sm font-medium text-amber-900">
              PO term &amp; condition deviation required. Please email the deviation
              table to the Procurement Team for review.
            </p>
            <div className="mt-3">
              <Label>Summary of the deviation (optional)</Label>
              <TextArea
                value={state.terms.purchaseOrderTermsDeviationNotes}
                onChange={v =>
                  updateNested(['terms', 'purchaseOrderTermsDeviationNotes'], v)
                }
              />
              <p className="mt-1 text-xs text-amber-800">
                You can still submit this application. Procurement will review the
                deviation before your prequalification is approved.
              </p>
            </div>
          </div>
        )}

        <FieldError show={fieldErrors.has('terms.purchaseOrderTermsStatus')}>
          Please select whether you accept the Standard Purchase Order Terms and
          Conditions, or require a deviation.
        </FieldError>
      </div>
  );

  // Banking details are collected for every prequalification type (including
  // Basic) — GRPS needs payment details to set the supplier up regardless of
  // risk tier. Shared between the Basic step and the Low/High-risk Commercial
  // step so the markup and validation stay in one place.
  const renderBankingDetails = () => (
    <Section title="Commercial: Banking Details">
      {(() => {
        const au = isAustralianAccount(
          state.banking.countryOfAccount,
          state.headOffice.country
        );
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label required>Account Name</Label>
              <TextInput
                value={state.banking.accountName}
                onChange={v => updateNested(['banking', 'accountName'], v)}
                invalid={fieldErrors.has('banking.accountName')}
              />
            </div>
            <div>
              <Label>Bank</Label>
              <TextInput
                value={state.banking.bank}
                onChange={v => updateNested(['banking', 'bank'], v)}
              />
            </div>
            <div>
              <Label>Country of Bank Account</Label>
              <TextInput
                value={state.banking.countryOfAccount}
                onChange={v => updateNested(['banking', 'countryOfAccount'], v)}
                placeholder={state.headOffice.country || 'Australia'}
              />
            </div>
            {au ? (
              <>
                <div>
                  <Label required>BSB</Label>
                  <TextInput
                    value={state.banking.bsb}
                    onChange={v => updateNested(['banking', 'bsb'], digitsOnly(v))}
                    placeholder="6 digits"
                    invalid={fieldErrors.has('banking.bsb')}
                  />
                </div>
                <div>
                  <Label required>Account Number</Label>
                  <TextInput
                    value={state.banking.accountNumber}
                    onChange={v => updateNested(['banking', 'accountNumber'], digitsOnly(v))}
                    placeholder="6-9 digits"
                    invalid={fieldErrors.has('banking.accountNumber')}
                  />
                </div>
              </>
            ) : (
              <>
                <div>
                  <Label>Institution Number</Label>
                  <TextInput
                    value={state.banking.institutionNumber}
                    onChange={v => updateNested(['banking', 'institutionNumber'], digitsOnly(v))}
                    placeholder="e.g. Canada: 3 digits"
                  />
                </div>
                <div>
                  <Label>Transit / Routing Number</Label>
                  <TextInput
                    value={state.banking.transitNumber}
                    onChange={v => updateNested(['banking', 'transitNumber'], digitsOnly(v))}
                    placeholder="e.g. Canada: 5 digits"
                  />
                </div>
                <div>
                  <Label required>Account Number</Label>
                  <TextInput
                    value={state.banking.accountNumber}
                    onChange={v => updateNested(['banking', 'accountNumber'], alnumOnly(v))}
                    placeholder="Letters and numbers only"
                    invalid={fieldErrors.has('banking.accountNumber')}
                  />
                </div>
                <div>
                  <Label>SWIFT / BIC</Label>
                  <TextInput
                    value={state.banking.swift}
                    onChange={v => updateNested(['banking', 'swift'], alnumOnly(v))}
                  />
                </div>
                <div>
                  <Label>IBAN</Label>
                  <TextInput
                    value={state.banking.iban}
                    onChange={v => updateNested(['banking', 'iban'], alnumOnly(v))}
                  />
                </div>
              </>
            )}
          </div>
        );
      })()}
    </Section>
  );

  const renderCommercialStep = () => {
    if (state.prequalificationType === 'Basic') {
      return (
        <>
          <Section title="Part 2 — Commercial">
            <p className="text-gray-600 mb-4">
              Basic prequalification asks for your banking details and your answer on our
              Standard Purchase Order Terms &amp; Conditions.
            </p>
            {renderPurchaseOrderTerms()}
          </Section>
          {renderBankingDetails()}
        </>
      );
    }
    return (
      <>
        <Section title="Part 2 — Commercial: Terms of Supply">
          <div className="space-y-4">
            <div>
              <Label>
                Is your organisation or any of your Directors involved in active or pending
                litigation that could affect providing goods or services to GRPS?
              </Label>
              <YesNo
                value={state.terms.hasActiveLitigation}
                onChange={v => updateNested(['terms', 'hasActiveLitigation'], v)}
              />
            </div>
            {state.terms.hasActiveLitigation && (
              <>
                <div>
                  <Label>Provide details</Label>
                  <TextArea
                    value={state.terms.litigationDetails}
                    onChange={v => updateNested(['terms', 'litigationDetails'], v)}
                  />
                </div>
                <div>
                  <Label>Upload supporting files</Label>
                  <FileDropZone
                    fieldKey="litigationAttachments"
                    multiple
                    onFiles={onFileChange}
                  />
                  {(files.litigationAttachments || []).map((f, i) => (
                    <FileRow key={i} f={f} onRemove={() => removeFile('litigationAttachments', i)} />
                  ))}
                </div>
              </>
            )}
            {state.prequalificationType === 'High-risk' && (
              <div className="rounded-lg border border-gray-200 p-4">
                <Label>GRPS Service Agreement</Label>
                <p className={`mb-2 ${TEXT.bodyMuted}`}>
                  GRPS will send you its Service Agreement separately by email. If you accept it,
                  you do not need to answer the Standard Purchase Order Terms and Conditions.
                </p>
                <label className="flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    className={`mt-1 ${CHECKBOX_CLASS}`}
                    checked={!!state.terms.serviceAgreementAccepted}
                    onChange={e => updateNested(['terms', 'serviceAgreementAccepted'], e.target.checked)}
                  />
                  <span>I accept the GRPS Service Agreement, sent to me separately by email.</span>
                </label>
              </div>
            )}
            {!(state.prequalificationType === 'High-risk' && state.terms.serviceAgreementAccepted) &&
              renderPurchaseOrderTerms()}
          </div>
        </Section>

        <Section title="Commercial: Insurance">
          <p className={`mb-4 ${TEXT.helper}`}>
            GRPS requires the following insurances with the minimum limits of coverage as
            indicated below as it applies to the nature of the work, location and contract
            worker. Please identify if your Company meets each requirement, and upload a current
            Certificate of Currency.
          </p>
          {INSURANCE_TYPES.map(({ key, label, minimum, requiredWhen }) => (
            <div key={key} className="border-t pt-4 mt-4 first:border-0 first:mt-0 first:pt-0">
              <div className="font-medium mb-2 text-blue-900">
                {label}
                {isInsuranceRequired(key) && (
                  <span className="ml-2">
                    <QueueChip tone="red">
                      Required{key.startsWith('professionalIndemnity') ? ' (standard or sole-trader)' : ''}
                    </QueueChip>
                  </span>
                )}
              </div>
              {requiredWhen && (
                <div className={`italic mb-2 ${TEXT.captionMuted}`}>
                  Required when: {requiredWhen}
                </div>
              )}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-3 text-sm">
                <div className={`mb-1 ${TEXT.overline}`}>
                  Minimum Limits (AUD or equivalent)
                </div>
                <div className="text-gray-800">{minimum}</div>
              </div>
              <div className="mb-3">
                <Label>Does your coverage meet this minimum?</Label>
                <YesNo
                  value={state.insurances[key].meetsMinimum}
                  onChange={v => updateNested(['insurances', key, 'meetsMinimum'], v)}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                <div>
                  <Label>Policy expiry date</Label>
                  <input
                    type="date"
                    value={state.insurances[key].expiryDate}
                    onChange={e => updateNested(['insurances', key, 'expiryDate'], e.target.value)}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-base"
                  />
                  <FieldError show={fieldErrors.has(`insurance.${key}.expiryDate`)}>
                    Please give the expiry date shown on the Certificate of Currency.
                  </FieldError>
                </div>
                <div>
                  <Label>Insurer</Label>
                  <input
                    type="text"
                    value={state.insurances[key].insurer}
                    onChange={e => updateNested(['insurances', key, 'insurer'], e.target.value)}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-base"
                  />
                  {/* Only these two: GreenTree has no company column for
                      professional indemnity, so nothing caps it there. */}
                  {(key === 'generalThirdPartyLiability' || key === 'employersLiability') && (
                    <GtLengthHint
                      value={state.insurances[key].insurer}
                      max={GT_INSURER_NAME_MAX}
                    />
                  )}
                </div>
                <div>
                  <Label>Policy number</Label>
                  <input
                    type="text"
                    value={state.insurances[key].policyNumber}
                    onChange={e => updateNested(['insurances', key, 'policyNumber'], e.target.value)}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-base"
                  />
                  {(key === 'generalThirdPartyLiability' || key === 'employersLiability') && (
                    <GtLengthHint
                      value={state.insurances[key].policyNumber}
                      max={GT_POLICY_NUMBER_MAX}
                    />
                  )}
                </div>
              </div>
              <div>
                <Label>Certificate of Currency</Label>
                <FileDropZone
                  fieldKey={`insurance.${key}`}
                  accept=".pdf,.png,.jpg,.jpeg"
                  onFiles={onFileChange}
                />
                {(files[`insurance.${key}`] || []).map((f, i) => (
                  <FileRow
                    key={i}
                    f={f}
                    onRemove={() => removeFile(`insurance.${key}`, i)}
                  />
                ))}
                <FieldError
                  show={
                    fieldErrors.has(`insurance.${key}`) ||
                    (key === 'professionalIndemnitySoleTrader' &&
                      fieldErrors.has('insurance.professionalIndemnity'))
                  }
                >
                  This insurance is required — confirm the minimum and attach a Certificate of
                  Currency.
                </FieldError>
              </div>
            </div>
          ))}
          <div className="mt-6 border-t pt-4">
            <Label>Additional Insurance Details (optional)</Label>
            <TextArea
              value={state.additionalInsuranceDetails}
              onChange={v => updateField('additionalInsuranceDetails', v)}
            />
          </div>
        </Section>

        <Section title="Commercial: ISO Certificates, Licences and Accreditations">
          <p className={`mb-2 ${TEXT.helper}`}>
            Upload any supporting ISO certificates, trade licences (Electrical, Gas Fitting,
            Pressure Testing, NDT, etc.) or accreditations.
          </p>
          <FileDropZone fieldKey="isoLicences" multiple onFiles={onFileChange} />
          {(files.isoLicences || []).map((f, i) => (
            <FileRow key={i} f={f} onRemove={() => removeFile('isoLicences', i)} />
          ))}
        </Section>

        {renderBankingDetails()}

        <Section title="Commercial: Experience">
          <p className={`mb-2 ${TEXT.helper}`}>
            List recent contracts or projects relevant to the goods or services you&apos;ll supply.
          </p>
          {state.experience.map((row, idx) => (
            <div key={idx} className="border border-gray-200 rounded-lg p-3 mb-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label>Contract / Project</Label>
                  <TextInput
                    value={row.contractProject}
                    onChange={v => {
                      const next = [...state.experience];
                      next[idx] = { ...row, contractProject: v };
                      updateField('experience', next);
                    }}
                  />
                </div>
                <div>
                  <Label>Client</Label>
                  <TextInput
                    value={row.client}
                    onChange={v => {
                      const next = [...state.experience];
                      next[idx] = { ...row, client: v };
                      updateField('experience', next);
                    }}
                  />
                </div>
                <div className="md:col-span-2">
                  <Label>Goods / Services Supplied</Label>
                  <TextInput
                    value={row.goodsServices}
                    onChange={v => {
                      const next = [...state.experience];
                      next[idx] = { ...row, goodsServices: v };
                      updateField('experience', next);
                    }}
                  />
                </div>
                <div>
                  <Label>Timeline</Label>
                  <TextInput
                    value={row.timeline}
                    onChange={v => {
                      const next = [...state.experience];
                      next[idx] = { ...row, timeline: v };
                      updateField('experience', next);
                    }}
                  />
                </div>
                <div>
                  <Label>Contract Value (AUD)</Label>
                  <TextInput
                    value={row.contractValue}
                    onChange={v => {
                      const next = [...state.experience];
                      next[idx] = { ...row, contractValue: v };
                      updateField('experience', next);
                    }}
                  />
                </div>
              </div>
              {state.experience.length > 1 && (
                <Button variant="linkDanger" size="sm" onClick={() =>
                    updateField(
                      'experience',
                      state.experience.filter((_, i) => i !== idx)
                    )
                  } className="mt-2">
                  Remove
                </Button>
              )}
            </div>
          ))}
          <Button variant="link" size="sm" onClick={() => updateField('experience', [...state.experience, emptyExperienceRow()])}>
            + Add another entry
          </Button>
        </Section>

        <Section title="Commercial: Sub-Subcontractors">
          <div>
            <Label>Are sections of the work sublet/subcontracted to other parties?</Label>
            <YesNo
              value={state.subSubcontractors.sublets}
              onChange={v => updateNested(['subSubcontractors', 'sublets'], v)}
            />
          </div>
          {state.subSubcontractors.sublets && (
            <div className="mt-3">
              <Label>Provide details</Label>
              <TextArea
                value={state.subSubcontractors.details}
                onChange={v => updateNested(['subSubcontractors', 'details'], v)}
              />
            </div>
          )}
        </Section>
      </>
    );
  };

  const renderCorporateStep = () => {
    if (state.prequalificationType === 'Basic') {
      return (
        <Section title="Part 3 — Corporate Responsibility">
          <div className="font-medium mb-2 text-blue-900">Public Liability Insurance</div>
          <p className={`mb-4 ${TEXT.helper}`}>
            GR Production Services requires its suppliers to hold current Public Liability
            insurance. Please confirm whether your business holds this cover and, if it does,
            attach a current Certificate of Currency.
          </p>
          <div className="mb-3">
            <Label required>Does your business hold current Public Liability insurance?</Label>
            <YesNo
              value={state.insurances.generalThirdPartyLiability.held ?? null}
              onChange={v => updateNested(['insurances', 'generalThirdPartyLiability', 'held'], v)}
            />
            <FieldError show={fieldErrors.has('insurances.generalThirdPartyLiability.held')}>
              Please select Yes or No.
            </FieldError>
          </div>
          {state.insurances.generalThirdPartyLiability.held === true && (
            <div>
              <Label required>Certificate of Currency</Label>
              <FileDropZone
                fieldKey="insurance.generalThirdPartyLiability"
                accept=".pdf,.png,.jpg,.jpeg"
                onFiles={onFileChange}
              />
              <FieldError show={fieldErrors.has('insurance.generalThirdPartyLiability')}>
                Please attach a current Certificate of Currency for your Public Liability insurance.
              </FieldError>
            </div>
          )}
          {/* Shown whatever the answer, so a file attached before switching to
              No can still be seen and removed rather than sent unseen. */}
          {(files['insurance.generalThirdPartyLiability'] || []).map((f, i) => (
            <FileRow
              key={i}
              f={f}
              onRemove={() => removeFile('insurance.generalThirdPartyLiability', i)}
            />
          ))}
          {state.insurances.generalThirdPartyLiability.held === false && (
            <Alert
              variant="warning"
              message="Without current Public Liability insurance, your application may not be approved. You may still submit it, and GRPS Procurement will contact you if anything further is needed."
            />
          )}
        </Section>
      );
    }
    return (
      <Section title="Part 3 — Corporate Responsibility">
        <p className={`mb-1 ${TEXT.helper}`}>
          Please tick each statement to acknowledge and agree. <span className="text-red-600">*</span>
        </p>
        <FieldError show={fieldErrors.has('corporateResponsibilityAcceptances')}>
          Please tick every corporate responsibility statement to acknowledge and agree.
        </FieldError>
        <div className="mt-3">
          {CORPORATE_RESPONSIBILITY_STATEMENTS.map((stmt, idx) => (
            <label key={idx} className="flex items-start gap-2 mb-3">
              <input
                type="checkbox"
                className={`mt-0.5 shrink-0 ${CHECKBOX_CLASS}`}
                checked={state.corporateResponsibilityAcceptances[idx]}
                onChange={e => {
                  const next = [...state.corporateResponsibilityAcceptances];
                  next[idx] = e.target.checked;
                  updateField('corporateResponsibilityAcceptances', next);
                }}
              />
              <span className="text-sm">{stmt}</span>
            </label>
          ))}
        </div>
      </Section>
    );
  };

  const renderReviewStep = () => (
    <Section title="Review & Submit">
      <p className="text-gray-600 mb-4">
        Review your submission below and click Submit when ready. You will not be able to edit
        this submission after it is sent; GRPS Procurement will contact you if any clarification
        is needed.
      </p>
      <div className="text-sm space-y-1">
        <div>
          <strong>Type:</strong> {state.prequalificationType}
        </div>
        <div>
          <strong>Legal Name:</strong> {state.company.legalName}
        </div>
        <div>
          <strong>ABN:</strong> {state.company.abn}
        </div>
        <div>
          <strong>GRPS Contact:</strong> {state.grpsContactName}
        </div>
        <div>
          <strong>Files attached:</strong>{' '}
          {Object.values(files).reduce((acc, arr) => acc + arr.length, 0)}
        </div>
      </div>
      {error && <Alert variant="error" message={error} className="mt-4" />}
      <div className="mt-6 flex gap-3">
        <Button variant="primary" disabled={submitting} onClick={onSubmit}>
          {submitting
            ? adminEditId ? 'Saving…' : 'Submitting…'
            : adminEditId ? 'Save Changes' : 'Submit application'}
        </Button>
      </div>
    </Section>
  );

  // Honeypot input (hidden from users)
  const Honeypot = () => (
    <input
      type="text"
      name="website_trap"
      tabIndex={-1}
      autoComplete="off"
      className="hidden"
      aria-hidden="true"
    />
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-blue-900 text-white py-4">
        <div className="max-w-4xl mx-auto px-4">
          <h1 className="text-2xl font-semibold">GR Production Services — Vendor Prequalification</h1>
          <p className="text-sm text-blue-100">
            Valid for 3 years upon approval. Questions:{' '}
            <a className="underline" href="mailto:procurement@grps.com.au">
              procurement@grps.com.au
            </a>
          </p>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {adminEditId && (
          <Alert variant="warning" message={
            <>
              <strong>Admin edit mode</strong> — you are editing an existing submission
              (<code className="text-xs">{adminEditId}</code>). Changes save back to the same
              record; the submission&apos;s status is preserved.
            </>
          } className="mb-4" />
        )}
        {/* Step indicator — click any step to jump directly. On narrow screens
            the buttons keep a minimum width and the row scrolls horizontally;
            from md they distribute evenly across the width. */}
        <div className="flex items-stretch gap-2 mb-6 overflow-x-auto pb-1 -mx-1 px-1">
          {steps.map((s, i) => (
            <button
              key={s}
              type="button"
              onClick={() => goToStep(i)}
              className={`flex-shrink-0 min-w-[6.5rem] md:flex-1 md:min-w-0 text-center text-xs py-2 px-2 rounded-md transition cursor-pointer whitespace-nowrap ${
                i === step
                  ? 'bg-blue-700 text-white'
                  : i < step
                    ? 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
              }`}
            >
              {i + 1}. {s}
            </button>
          ))}
        </div>

        {/* Dev-only "TEST FILL ME" helper */}
        {IS_DEV && (
          <div className="mb-4 flex justify-end">
            <Button variant="warning" size="xs" onClick={fillTestData} title="Fill every field with dummy test data (dev only)">
              🧪 TEST FILL ME
            </Button>
          </div>
        )}

        <Honeypot />

        {draftHydrating && (
          <Alert variant="info" message="Loading your saved application…" className="mb-4" />
        )}
        {draftHydrateError && (
          <Alert variant="error" message={draftHydrateError} className="mb-4" />
        )}

        {sentBack && (
          <Alert
            variant={sentBack.kind === 'rejected' ? 'error' : 'warning'}
            title={
              sentBack.kind === 'rejected'
                ? 'Your application was not approved as submitted. Please make the changes below and resubmit:'
                : 'GRPS Procurement needs more information before your application can be approved:'
            }
            message={<div className="whitespace-pre-wrap break-words">{sentBack.message}</div>}
            className="mb-4"
          />
        )}

        {restoredFromLocal && (
          <Alert
            variant="warning"
            className="mb-4"
            message={restoredDraftFiles > 0
                ? `We restored the answers you'd previously entered on this device, and the ${restoredDraftFiles} file${
                    restoredDraftFiles === 1 ? '' : 's'
                  } you had saved. Please check them before you submit.`
                : 'We restored the answers you\'d previously entered on this device. Any file attachments will need to be added again before you submit.'}
            actions={
              <div className="flex items-center gap-4">
                <Button variant="link" size="sm" onClick={() => setRestoredFromLocal(false)}>
                  Dismiss
                </Button>
                <Button variant="linkDanger" size="sm" onClick={discardRestoredDraft}>
                  Start over
                </Button>
              </div>
            }
          />
        )}

        {draftToken && draftAttachments.length > 0 && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
            <div className="text-sm font-medium text-green-800 mb-1">
              Previously uploaded files ({draftAttachments.length})
            </div>
            <div className="text-xs text-green-700 mb-2">
              These will be included with your submission. Remove any you don&apos;t want.
            </div>
            <ul className="text-sm text-green-800 space-y-1">
              {draftAttachments.map(a => (
                <li key={a._id} className="flex items-center gap-2">
                  <span className="text-gray-700">📎 {a.filename}</span>
                  <span className={TEXT.caption}>
                    ({Math.round((a.size || 0) / 1024)} KB · {a.fieldKey})
                  </span>
                  <Button variant="linkDanger" size="xs" onClick={() => removeDraftAttachment(a._id)}>
                    remove
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {(() => {
          const name = steps[step];
          switch (name) {
            case 'Type':
              return renderTypeStep();
            case 'Company Profile':
              return renderCompanyStep();
            case 'Commercial':
              return renderCommercialStep();
            case 'Corporate Responsibility':
              return renderCorporateStep();
            case 'HSE':
              return (
                <>
                  {SECTION_DOCUMENTS_NOTE}
                  <HighRiskForm
                    parts={HIGH_RISK_PARTS.filter(p => p.key === 'hse')}
                    values={highRisk}
                    files={files}
                    onChange={(key, value) => setHighRisk(s => ({ ...s, [key]: value }))}
                    onFileChange={(key, fl, multiple) => onFileChange(key, fl, multiple)}
                    onRemoveFile={removeFile}
                  />
                </>
              );
            case 'Quality':
              return (
                <>
                  {SECTION_DOCUMENTS_NOTE}
                  <HighRiskForm
                    parts={HIGH_RISK_PARTS.filter(p => p.key === 'quality')}
                    values={highRisk}
                    files={files}
                    onChange={(key, value) => setHighRisk(s => ({ ...s, [key]: value }))}
                    onFileChange={(key, fl, multiple) => onFileChange(key, fl, multiple)}
                    onRemoveFile={removeFile}
                  />
                </>
              );
            case 'IR':
              return (
                <>
                  {SECTION_DOCUMENTS_NOTE}
                  <HighRiskForm
                    parts={HIGH_RISK_PARTS.filter(p => p.key === 'ir')}
                    values={highRisk}
                    files={files}
                    onChange={(key, value) => setHighRisk(s => ({ ...s, [key]: value }))}
                    onFileChange={(key, fl, multiple) => onFileChange(key, fl, multiple)}
                    onRemoveFile={removeFile}
                  />
                </>
              );
            case 'Review':
              return renderReviewStep();
            default:
              return null;
          }
        })()}

        <div className="flex flex-wrap items-center justify-between gap-2 mt-6">
          <Button variant="outline" onClick={() => goToStep(step - 1)} disabled={step === 0}>
            Back
          </Button>
          <div className="flex items-center gap-2">
            {!adminEditId && (
              <Button variant="outline" onClick={onSaveAndCloseClick} disabled={savingDraft || submitting} title="Save your progress and email yourself a link to continue later">
                {savingDraft ? 'Saving…' : 'Save & Close'}
              </Button>
            )}
            {step < steps.length - 1 && (
              <Button variant="primary" onClick={() => goToStep(step + 1)}>
                Next
              </Button>
            )}
          </div>
        </div>
        {error && step < steps.length - 1 && (
          <Alert variant="error" message={error} className="mt-3" />
        )}
      </div>

      {showSaveModal && (
        <SaveEmailPromptModal
          initialEmail={saveModalEmail}
          onCancel={() => setShowSaveModal(false)}
          onConfirm={email => {
            setSaveModalEmail(email);
            // Also write it back into the primary designated contact so they
            // don't have to re-enter when they resume.
            setState(s => {
              const contacts = s.designatedContacts.length
                ? [...s.designatedContacts]
                : [emptyDesignatedContact(true)];
              let pIdx = contacts.findIndex(c => c.isPrimary);
              if (pIdx < 0) pIdx = 0;
              contacts[pIdx] = { ...contacts[pIdx], email };
              return { ...s, designatedContacts: contacts };
            });
            void runSaveDraft(email);
          }}
        />
      )}

      {saveResult && (
        <SaveResultModal
          link={saveResult.link}
          sentTo={saveResult.sentTo}
          emailSent={saveResult.emailSent}
          onClose={() => setSaveResult(null)}
        />
      )}
    </div>
  );
}

function SaveEmailPromptModal({
  initialEmail,
  onCancel,
  onConfirm,
}: {
  initialEmail: string;
  onCancel: () => void;
  onConfirm: (email: string) => void;
}) {
  const [value, setValue] = useState(initialEmail);
  const [err, setErr] = useState('');
  return (
    <Modal
      isOpen
      onClose={onCancel}
      title="Where should we send your resume link?"
      description="Enter an email address. We'll save your progress and email you a secure link to continue this application later."
      size="sm"
    >
      <ModalBody>
        <input
          type="email"
          value={value}
          onChange={e => setValue(e.target.value)}
          placeholder="you@example.com"
          autoFocus
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        {err && <div className="mt-2 text-sm text-red-600">{err}</div>}
      </ModalBody>
      <ModalFooter>
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button variant="primary" onClick={() => {
            const v = value.trim();
            if (!v || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
              setErr('Please enter a valid email address.');
              return;
            }
            onConfirm(v);
          }}>
          Save & Email Link
        </Button>
      </ModalFooter>
    </Modal>
  );
}

function SaveResultModal({
  link,
  sentTo,
  emailSent,
  onClose,
}: {
  link: string;
  sentTo: string;
  emailSent: boolean;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Your progress is saved"
      size="sm"
    >
      <ModalBody>
        {emailSent ? (
          <p className={TEXT.body}>
            A secure link to continue this application has been emailed to{' '}
            <strong>{sentTo}</strong>. It is valid for <strong>90 days</strong> from your most
            recent save.
          </p>
        ) : (
          <p className={TEXT.warningText}>
            We saved your application, but the email to <strong>{sentTo}</strong> could not be sent
            just now. Copy the link below to keep your access — it won&apos;t be shown again.
          </p>
        )}
        <div className="mt-3 p-2 bg-gray-50 border border-gray-200 rounded-md text-xs break-all">
          {link}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button variant="outline" size="xs" onClick={async () => {
            try {
              await navigator.clipboard.writeText(link);
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            } catch (_e) {
              // ignore
            }
          }}>
          {copied ? 'Copied!' : 'Copy link'}
        </Button>
        <Button variant="primary" size="sm" onClick={onClose}>
          Done
        </Button>
      </ModalFooter>
    </Modal>
  );
}

function FileRow({ f, onRemove }: { f: File; onRemove: () => void }) {
  return (
    <div className="flex items-center gap-2 mt-1 text-sm">
      <span className="text-gray-700">📎 {f.name}</span>
      <span className="text-gray-500">({Math.round(f.size / 1024)} KB)</span>
      <Button variant="linkDanger" size="xs" onClick={onRemove}>
        remove
      </Button>
    </div>
  );
}
